// =========================================================================
// SightLine Networks — Production Website
// React + Tailwind CSS | Single-file implementation
//
// SEO: Each page has unique H1, meta title/description, canonical,
//      OG/Twitter tags, hreflang alternates, and JSON-LD schema managed by
//      SeoManager.
//
// i18n: The site ships in English (/) and Spanish (/es/...). Every URL is
//       defined once in src/i18n/routes.ts; all visible copy goes through
//       t("English source string") and is translated in src/i18n/es.ts.
//       Missing Spanish entries fall back to English rather than breaking.
//
// Google Ads: CTA buttons carry data-cta and data-page attributes.
//             trackCta() pushes to dataLayer (GTM) and gtag (GA4).
//             Form submissions fire a conversion-friendly event.
//
// Integration notes:
//   - Connect ContactForm to EmailJS / Supabase / your backend
//   - Add GTM container snippet in public/index.html <head>
//   - Add GA4 config snippet or load via GTM
//   - Add Google Ads conversion linker + conversion tags in GTM
// =========================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronRight,
  Shield,
  Globe,
  Server,
  Monitor,
  Menu,
  X,
  CheckCircle2,
  MapPin,
  Headphones,
  Clock3,
  BadgeCheck,
  LineChart,
  Lock,
  Phone,
  Mail,
  Wifi,
  Camera,
  Cloud,
  ArrowRight,
  Check,
  Zap,
  HardDrive,
  Wrench,
  Building2,
  Languages,
  ChevronDown,
  Router,
  Network,
  Cable,
  FileText,
  Search,
} from "lucide-react";
import {
  BASE_URL,
  LANGS,
  LanguageProvider,
  SEO_CONFIG,
  htmlLangAttr,
  ogLocale,
  pathFor,
  resolveInitialLocation,
  resolveLocation,
  translate,
  urlFor,
  useLanguage,
  useT,
  writeLangPreference,
} from "./i18n";
import type { Lang, PageKey } from "./i18n";
// Conversion landing pages live in their own modules so the (very large)
// marketing bundle file stays navigable. Each also exports its FAQ array so
// buildSchema() can emit a FAQPage JSON-LD from the same single source the
// visible accordion renders — no hand-mirrored schema blocks anywhere.
import {
  BusinessWifiLanding,
  businessWifiFaqs,
} from "./landing/BusinessWifiLanding";
import {
  UnifiInstallerLanding,
  unifiFaqs,
} from "./landing/UnifiInstallerLanding";
import {
  CameraInstallationLanding,
  cameraFaqs,
} from "./landing/CameraInstallationLanding";
import {
  StructuredCablingLanding,
  cablingFaqs,
} from "./landing/StructuredCablingLanding";

// =========================================================================
// TYPES
// =========================================================================

// PageKey and Lang now live in src/i18n/types.ts because the routing table,
// the SEO config, and the sitemap generator all key off them.

type HeaderProps = {
  setPage: (page: PageKey) => void;
  page: PageKey;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
};

// =========================================================================
// CONSTANTS
// =========================================================================

const PHONE = "(727) 283-6452";
const PHONE_HREF = "tel:17272836452";
const EMAIL = "info@sightlinenetworks.com";

// GA4 measurement ID (public identifier, not a secret — same as its use in
// public/index.html's gtag config). Custom events below are sent directly to
// this property ONLY (via `send_to`), not broadcast to every configured gtag
// target on the page. Without that restriction, calling gtag('event', ...)
// here would also fire straight into the Google Ads account
// (AW-17971699331, also configured on this page), duplicating the
// generate_lead conversion GTM already fires from its own element-visibility
// trigger on the lead-form success message.
const GA4_MEASUREMENT_ID = "G-L3HMVQZ9HS";

// Privacy notice banner — bump CONSENT_VERSION to re-show the banner to
// everyone after a material change to our data/cookie practices.
const CONSENT_VERSION = 1;
const CONSENT_COOKIE_NAME = "sln-privacy-consent";

// Consent is stored in a cookie scoped to the registrable parent domain so the
// acknowledgment is GLOBAL across every SightLine property (this marketing site
// and host.sightlinenetworks.com) rather than per-application. Keep this logic
// in sync with hosting-platform/src/utils/consent.js.
function consentDomainAttr(): string {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  // Share across *.sightlinenetworks.com in prod; host-only in dev/preview.
  return host === "sightlinenetworks.com" || host.endsWith(".sightlinenetworks.com")
    ? "; Domain=.sightlinenetworks.com"
    : "";
}

function hasAcknowledgedConsent(): boolean {
  if (typeof document === "undefined") return false;
  const match = document.cookie.match(/(?:^|;\s*)sln-privacy-consent=([^;]*)/);
  if (!match) return false;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return Boolean(parsed && parsed.acknowledged && parsed.version === CONSENT_VERSION);
  } catch {
    return false;
  }
}

function writeAcknowledgedConsent(): void {
  if (typeof document === "undefined") return;
  const value = encodeURIComponent(
    JSON.stringify({ acknowledged: true, version: CONSENT_VERSION })
  );
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${60 * 60 * 24 * 365}; Path=/` +
    `${consentDomainAttr()}; SameSite=Lax${secure}`;
}

// Home page FAQ — visible content in HomePage MUST stay in sync with the
// FAQPage JSON-LD in public/index.html for Google rich-result eligibility.
const HOME_FAQS: Array<{ q: string; a: string }> = [
  {
    q: "Are you a certified Ubiquiti UniFi installer in Tampa Bay?",
    a: "Yes. SightLine Networks designs and installs Ubiquiti UniFi systems for Tampa Bay businesses, including UniFi Network (switches, access points, gateways), UniFi Protect security cameras, and UniFi Access door control. We handle planning, deployment, configuration, and ongoing support.",
  },
  {
    q: "Do you install Cisco networking and Cisco VOIP for businesses?",
    a: "Yes. We install Cisco and Cisco Meraki networking equipment — switches, routers, firewalls, and wireless — plus Cisco VOIP phone systems for offices that need enterprise reliability and call routing.",
  },
  {
    q: "Can you set up a Synology NAS for local backup?",
    a: "Absolutely. Synology NAS is one of our recommended storage and backup platforms. We size the unit, configure Synology DSM, set up RAID and snapshots, integrate with cloud backup providers, and document the recovery procedure for your team.",
  },
  {
    q: "Which IP camera brands do you install?",
    a: "We install Ubiquiti UniFi Protect, Hikvision, Axis Communications, Dahua, Amcrest, and Reolink. We pick the brand based on your site, budget, and integration needs (NVR vs. cloud, indoor vs. outdoor, license-plate or analytics requirements).",
  },
  {
    q: "Which VOIP phone systems do you support?",
    a: "We deploy and support business VOIP phones from Yealink, Poly (Polycom), and Grandstream, plus Cisco VOIP for enterprise environments. We can integrate with cloud PBX providers or set up a local PBX.",
  },
  {
    q: "Do you only work in Tampa Bay?",
    a: "We're based in Clearwater, FL and primarily serve the Tampa Bay area — Tampa, Clearwater, St. Petersburg, Largo, Pinellas Park, and Brandon. Larger projects outside the immediate area can be discussed on a case-by-case basis.",
  },
  {
    q: "How much does a UniFi or security camera installation cost?",
    a: "Cost depends on site size, number of access points or cameras, cabling required, and whether existing infrastructure can be reused. Most projects fall between a few thousand dollars for small offices and tens of thousands for multi-building deployments. We provide free, itemized quotes after a brief site discussion.",
  },
  {
    q: "What happens during the free assessment?",
    a: "It starts with a short call about what's going on — slow Wi-Fi, dead zones, aging cameras, or day-to-day IT problems. When it makes sense, we visit your site, walk the space, and look at the equipment you already have. You then get a written quote with a clear scope, and there's no obligation to move forward.",
  },
  {
    q: "Can you fix Wi-Fi dead zones in an older or larger building?",
    a: "Yes. Concrete block walls, metal racking, and long distances all affect signal. We survey the building, plan access point placement around how your space is actually used, and install business-grade equipment instead of stacking consumer range extenders.",
  },
  {
    q: "Do you take over networks and IT that someone else set up?",
    a: "Yes. A lot of our work starts with cleaning up an existing setup — adopting equipment into a controller, fixing configuration problems, untangling cabling, and documenting what's there. We'll tell you honestly what's worth keeping and what isn't.",
  },
  {
    q: "Can I check my cameras from my phone when I'm away?",
    a: "Yes. The camera systems we install include remote viewing on your phone, tablet, or computer, and we walk you through the app and access setup before we consider the job done.",
  },
  {
    q: "Do you handle small jobs, like adding a few network drops?",
    a: "Yes. A few new cable drops, a rack cleanup, or a single access point run are normal parts of our week, and they get the same testing and labeling as large buildouts.",
  },
];

// =========================================================================
// SEO & TRACKING UTILITIES
// =========================================================================

function upsertMeta(
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string
) {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(
    `link[rel="${rel}"]`
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Publish the hreflang cluster for the current page.
 *
 * Both language versions of a page must point at each other (and at an
 * x-default) or Google treats them as unrelated near-duplicates instead of
 * alternates — which is what makes the Spanish tree rank for Spanish queries
 * without cannibalising the English one.
 */
function syncAlternateLinks(page: PageKey) {
  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());

  const alternates: Array<[string, string]> = [
    ["en", urlFor(page, "en")],
    ["es", urlFor(page, "es")],
    // English is the fallback for any locale we don't explicitly target.
    ["x-default", urlFor(page, "en")],
  ];

  alternates.forEach(([hreflang, href]) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    document.head.appendChild(el);
  });
}

// Landing-page FAQ arrays (single source: each src/landing/*.tsx module).
// buildSchema() emits a FAQPage node from the same array the page renders.
const LANDING_FAQS_BY_PAGE: Partial<
  Record<PageKey, Array<{ q: string; a: string }>>
> = {
  "business-wifi": businessWifiFaqs,
  "unifi-installer": unifiFaqs,
  "camera-installation": cameraFaqs,
  "structured-cabling": cablingFaqs,
};

function buildSchema(page: PageKey, lang: Lang) {
  const seo = SEO_CONFIG[lang];
  const business = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${BASE_URL}/#business`,
    name: "SightLine Networks",
    // Registered business entity name, distinct from the public-facing brand
    // name above — schema.org's dedicated property for this.
    legalName: "SightLine Networks LLC",
    url: BASE_URL,
    telephone: "+1-727-283-6452",
    email: EMAIL,
    areaServed: [
      "Tampa",
      "Largo",
      "Clearwater",
      "St. Petersburg",
      "Pinellas Park",
      "Brandon",
      "Tampa Bay",
    ],
    description: seo[page].description,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Clearwater",
      addressRegion: "FL",
      addressCountry: "US",
    },
    serviceType: [
      "Managed IT Services",
      "Network Installation",
      "Business Web Hosting",
      "Website Development",
      "Security Camera Systems",
      "Server and Cloud Support",
      "IT Consulting",
      "Ubiquiti UniFi Installation",
      "UniFi Protect Installation",
      "Cisco Network Installation",
      "Synology NAS Installation",
      "VOIP Phone Systems",
    ],
    brand: [
      "Ubiquiti", "UniFi", "UniFi Protect", "UniFi Network", "UniFi Access",
      "Cisco", "Cisco Meraki", "Synology",
      "Hikvision", "Axis Communications", "Dahua", "Amcrest", "Reolink",
      "Yealink", "Poly", "Grandstream",
    ].map((b) => ({ "@type": "Brand", name: b })),
    // Carried over from the old static LocalBusiness block in index.html so
    // nothing ships thinner than before SeoManager took over emission.
    logo: `${BASE_URL}/sightline-logo.png`,
    image: `${BASE_URL}/sightline-logo.png`,
    priceRange: "$$",
    sameAs: ["https://g.page/r/CfgnSightLine"],
    knowsAbout: [
      "Ubiquiti UniFi installation",
      "UniFi Protect security cameras",
      "UniFi Network design",
      "Cisco networking",
      "Cisco Meraki",
      "Cisco VOIP",
      "Synology NAS",
      "Synology DSM",
      "IP security cameras",
      "Hikvision installation",
      "Axis Communications cameras",
      "Dahua surveillance",
      "Yealink VOIP phones",
      "Poly conferencing",
      "Grandstream PBX",
      "Managed IT Services",
      "Network Installation",
      "Business Web Hosting",
      "Website Development",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "IT Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Ubiquiti UniFi Network Installation",
            description:
              "Ubiquiti UniFi installation: UniFi Network design, access points, switches, and cloud management for business Wi-Fi and networking.",
            brand: { "@type": "Brand", name: "Ubiquiti UniFi" },
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "UniFi Protect Security Camera Installation",
            description:
              "Professional UniFi Protect installation with NVR, IP cameras, remote viewing, and integration with UniFi Access.",
            brand: { "@type": "Brand", name: "Ubiquiti UniFi Protect" },
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Cisco Networking & VOIP",
            description:
              "Cisco and Cisco Meraki network installation, switches, routers, firewalls, and VOIP phone system deployment.",
            brand: { "@type": "Brand", name: "Cisco" },
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Synology NAS & Local Backup",
            description:
              "Synology NAS installation and configuration for local storage, business backup, and disaster recovery on Synology DSM.",
            brand: { "@type": "Brand", name: "Synology" },
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "IP Security Camera Installation",
            description:
              "Commercial IP camera systems from Hikvision, Axis, Dahua, Amcrest, and Reolink with NVR and remote viewing.",
            brand: [
              { "@type": "Brand", name: "Hikvision" },
              { "@type": "Brand", name: "Axis Communications" },
              { "@type": "Brand", name: "Dahua" },
            ],
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "VOIP Phone Systems",
            description:
              "Business VOIP phone systems from Yealink, Poly (Polycom), and Grandstream with PBX setup and configuration.",
            brand: [
              { "@type": "Brand", name: "Yealink" },
              { "@type": "Brand", name: "Poly" },
              { "@type": "Brand", name: "Grandstream" },
            ],
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Managed IT Services",
            description:
              "Proactive monitoring, helpdesk support, patch management, and ongoing technology guidance for Tampa Bay businesses.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Business Web Hosting & Website Development",
            description:
              "Managed web hosting with SSL and daily backups, plus custom responsive website development.",
          },
        },
      ],
    },
  };

  const inLanguage = lang === "es" ? "es-US" : "en-US";
  const nodes: any[] = [{ ...business, inLanguage }];

  if (page === "home") {
    // The FAQ block rendered on the home page, in the active language.
    nodes.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage,
      mainEntity: HOME_FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: translate(q, lang),
        acceptedAnswer: { "@type": "Answer", text: translate(a, lang) },
      })),
    });
    return nodes;
  }

  if (page === "hosting") {
    nodes.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: translate("Business Web Hosting", lang),
      provider: { "@type": "ProfessionalService", "@id": `${BASE_URL}/#business` },
      areaServed: "Tampa Bay",
      inLanguage,
      description: seo.hosting.description,
      offers: [
        { "@type": "Offer", name: "Starter Hosting", price: "29", priceCurrency: "USD" },
        { "@type": "Offer", name: "Business Hosting", price: "59", priceCurrency: "USD" },
        { "@type": "Offer", name: "Pro Hosting", price: "99", priceCurrency: "USD" },
      ],
    });
    return nodes;
  }

  const servicePages: PageKey[] = ["managed-it", "managed-it-services", "network-installation", "security-cameras", "server-cloud", "website-development", "it-consulting", "business-wifi", "unifi-installer", "camera-installation", "structured-cabling"];
  if (servicePages.includes(page)) {
    const brandsByPage: Partial<Record<PageKey, string[]>> = {
      "network-installation": ["Ubiquiti UniFi", "Ubiquiti", "Cisco", "Cisco Meraki"],
      "security-cameras": ["Ubiquiti UniFi Protect", "Hikvision", "Axis Communications", "Dahua", "Amcrest", "Reolink"],
      "server-cloud": ["Synology", "Dell", "HPE"],
      "managed-it": ["Ubiquiti UniFi", "Cisco", "Synology", "Microsoft"],
      "managed-it-services": ["Ubiquiti UniFi", "Cisco", "Synology", "Microsoft"],
      "business-wifi": ["Ubiquiti UniFi", "Cisco"],
      "unifi-installer": ["Ubiquiti UniFi", "UniFi Protect"],
      "camera-installation": ["Ubiquiti UniFi Protect", "Hikvision", "Axis Communications", "Dahua", "Amcrest", "Reolink"],
      // "structured-cabling": vendor-neutral by design — no brand list.
    };
    const brandList = brandsByPage[page];
    nodes.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: seo[page].ogTitle.replace(" | SightLine Networks", ""),
      provider: { "@type": "ProfessionalService", "@id": `${BASE_URL}/#business` },
      areaServed: "Tampa Bay",
      inLanguage,
      description: seo[page].description,
      ...(brandList && {
        brand: brandList.map((b) => ({ "@type": "Brand", name: b })),
      }),
    });

    nodes.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      inLanguage,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: translate("Home", lang), item: urlFor("home", lang) },
        { "@type": "ListItem", position: 2, name: seo[page].ogTitle.replace(" | SightLine Networks", ""), item: urlFor(page, lang) },
      ],
    });

    // Landing pages also carry an FAQ accordion; emit a FAQPage node from the
    // SAME array the page renders, translated for the active language.
    const landingFaqs = LANDING_FAQS_BY_PAGE[page];
    if (landingFaqs && landingFaqs.length > 0) {
      nodes.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        inLanguage,
        mainEntity: landingFaqs.map(({ q, a }) => ({
          "@type": "Question",
          name: translate(q, lang),
          acceptedAnswer: { "@type": "Answer", text: translate(a, lang) },
        })),
      });
    }
    return nodes;
  }

  if (page === "areas") {
    return [{ ...business, "@type": "LocalBusiness", inLanguage }];
  }

  return nodes;
}

function SeoManager({ page, lang }: { page: PageKey; lang: Lang }) {
  useEffect(() => {
    const c = SEO_CONFIG[lang][page];
    const canonical = urlFor(page, lang);

    document.title = c.title;
    document.documentElement.lang = htmlLangAttr(lang);

    upsertMeta('meta[name="description"]', "name", "description", c.description);
    if (c.keywords) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", c.keywords);
    }
    upsertMeta('meta[property="og:title"]', "property", "og:title", c.ogTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", c.ogDescription);
    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", ogLocale(lang));
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", c.ogTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", c.ogDescription);
    upsertLink("canonical", canonical);
    syncAlternateLinks(page);
    // Mirror the committed language out for trackCta(). Done in the effect
    // rather than during render so a discarded concurrent render can never
    // leave analytics reporting a language the user never saw.
    activeLang = lang;

    // public/index.html ships static English LocalBusiness + FAQPage JSON-LD
    // blocks. The FAQ block used to hand-mirror HOME_FAQS (a drift hazard);
    // buildSchema() now emits the FAQPage for home in BOTH languages from the
    // same HOME_FAQS array the accordion renders, so the static FAQ mirror is
    // always stripped. Every route gets a single injected #sln-schema graph
    // built for the actual page and language — one emission path, no copies.
    // The static blocks remain in index.html only as a no-JS fallback; they
    // are removed from the live DOM (and from every react-snap snapshot).
    const staticNodes = document.querySelectorAll(
      "#sln-static-business, #sln-static-faq"
    );
    staticNodes.forEach((el) => el.remove());

    const injected = document.getElementById("sln-schema") as HTMLScriptElement | null;
    const tag = injected ?? document.createElement("script");
    if (!injected) {
      tag.type = "application/ld+json";
      tag.id = "sln-schema";
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(buildSchema(page, lang));
  }, [page, lang]);

  return null;
}

// The active language, mirrored out of React state so trackCta() can report a
// correct page_location without threading `lang` through ~40 CTA call sites.
let activeLang: Lang = "en";

/** Push CTA events to GTM dataLayer and GA4 gtag */
function trackCta(action: string, label: string, page: PageKey) {
  if (typeof window === "undefined") return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "cta_click",
    action,
    label,
    page,
    language: activeLang,
    timestamp: new Date().toISOString(),
  });
  if (typeof w.gtag === "function") {
    w.gtag("event", action, {
      event_category: "engagement",
      event_label: label,
      language: activeLang,
      page_location: urlFor(page, activeLang),
      send_to: GA4_MEASUREMENT_ID,
    });
  }
}

// =========================================================================
// LEAD SUBMISSION
// =========================================================================
//
// Single shared submission path for every lead/quote form on the site
// (ContactPage and any Google Ads landing page's LeadForm). Keeping the
// endpoint + credential in one place means the Google Ads conversion — a GTM
// "element visibility" trigger keyed to the `p.text-emerald-600` success
// message — only ever needs to watch for that one class, from one code path,
// regardless of which form produced it.

const CONTACT_FUNCTION_URL =
  "https://jadtcuydqkdezwmvbdih.supabase.co/functions/v1/send-contact-email";
const CONTACT_FUNCTION_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImphZHRjdXlkcWtkZXp3bXZiZGloIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc3MTEwMjksImV4cCI6MjA4MzI4NzAyOX0.dhMk9TkTaKCv1DGKKf0f4IO3ZYNbXI2UwG3zg10FA28";

type LeadPayload = {
  name: string;
  company?: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  source: string;
};

// =========================================================================
// LEAD ATTRIBUTION
// =========================================================================

const ATTRIBUTION_STORAGE_KEY = "sln-lead-attribution";

type LeadAttribution = {
  landing_page: string;
  referrer: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
};

/**
 * Capture campaign attribution ONCE per session (first landing URL wins, so
 * mid-session navigation never overwrites the source that actually brought
 * the visitor in). Kept in sessionStorage and attached invisibly to whatever
 * lead form the visitor eventually submits. Called from the app shell on
 * mount — never from the form, so the capture happens even if the visitor
 * converts on a different page than they landed on.
 */
function captureLeadAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const get = (key: string) => params.get(key) || undefined;
    const attribution: LeadAttribution = {
      landing_page: window.location.pathname,
      referrer: document.referrer || "",
      utm_source: get("utm_source"),
      utm_medium: get("utm_medium"),
      utm_campaign: get("utm_campaign"),
      utm_term: get("utm_term"),
      utm_content: get("utm_content"),
      gclid: get("gclid"),
    };
    // Drop empty fields so stored payloads stay readable in lead email.
    (Object.keys(attribution) as Array<keyof LeadAttribution>).forEach((k) => {
      if (!attribution[k]) delete attribution[k];
    });
    window.sessionStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(attribution)
    );
  } catch {
    // sessionStorage can be unavailable (private browsing, blocked site data)
    // — attribution is a nice-to-have, never worth breaking a lead over.
  }
}

function readLeadAttribution(): LeadAttribution | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LeadAttribution) : undefined;
  } catch {
    return undefined;
  }
}

/** Post a lead to the send-contact-email edge function. Throws on failure. */
async function submitContactLead(payload: LeadPayload): Promise<void> {
  const attribution = readLeadAttribution();
  const res = await fetch(CONTACT_FUNCTION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${CONTACT_FUNCTION_ANON_KEY}`,
    },
    body: JSON.stringify({
      name: payload.name,
      company: payload.company || "",
      email: payload.email,
      phone: payload.phone || "",
      subject: payload.subject,
      message: payload.message,
      source: payload.source,
      // The edge function ignores unknown fields; attribution rides along for
      // anyone reading the admin notification email / future lead storage.
      ...(attribution ? { attribution } : {}),
    }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({} as { error?: string }));
    throw new Error(data.error || "Failed to send email");
  }
}

// =========================================================================
// LEAD CONVERSION TRACKING
// =========================================================================
//
// Success-only lead conversion event. Fired AFTER submitContactLead()
// resolves successfully — never on page load and never on a mere submit
// attempt — so GA4 / Google Ads `generate_lead` counts real submissions
// only. The dataLayer push keeps GTM fully in control (a single
// "generate_lead" trigger can serve Ads conversions, GA4 events, offline
// imports, etc.); the direct gtag call covers GA4 if GTM has no tag yet.
//
// The pre-existing GTM element-visibility trigger (p.text-emerald-600) and
// the form_submit-attempt event keep firing unchanged alongside this.

const LEAD_TYPE_BY_FORM_SOURCE: Record<string, string> = {
  "main-website": "quote_request",
  "managed-it-services-landing": "managed_it_consultation",
  "business-wifi-landing": "wifi_assessment",
  "unifi-installer-landing": "unifi_consultation",
  "camera-installation-landing": "camera_survey",
  "structured-cabling-landing": "cabling_estimate",
};

/** Success-only conversion push; call only after submitContactLead() resolves. */
function trackLeadConversion(source: string, page: PageKey) {
  if (typeof window === "undefined") return;
  const leadType = LEAD_TYPE_BY_FORM_SOURCE[source] || "quote_request";
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "generate_lead",
    lead_type: leadType,
    page,
    language: activeLang,
    timestamp: new Date().toISOString(),
  });
  if (typeof w.gtag === "function") {
    // send_to restricts this to GA4 only — the Ads conversion for a
    // successful lead is already fired by GTM's own element-visibility
    // trigger (see the comment on GA4_MEASUREMENT_ID above); without this,
    // every real lead would count as two conversions in Google Ads.
    w.gtag("event", "generate_lead", {
      lead_type: leadType,
      language: activeLang,
      page_location: urlFor(page, activeLang),
      send_to: GA4_MEASUREMENT_ID,
    });
  }
}

// Lightweight bot filtering, shared by every form. A hidden "company website"
// field that only bots fill in, plus a submission timestamp check — a real
// person cannot complete a human-visible form in under 1.5 seconds. Bots get
// a fake success state so they do not learn anything from retrying.
const HONEYPOT_FIELD = "website_url";
const MIN_FORM_FILL_MS = 1500;

function isSpamSubmission(honeypotValue: string, mountedAt: number): boolean {
  return honeypotValue.trim() !== "" || Date.now() - mountedAt < MIN_FORM_FILL_MS;
}

// =========================================================================
// SHARED UI COMPONENTS
// =========================================================================

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-700">
      {children}
    </p>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  text,
  to,
  setPage,
}: {
  icon: typeof Globe;
  title: string;
  text: string;
  /** Destination page for the "Learn more" link — renders a real `<a href>`. */
  to?: PageKey;
  setPage?: (page: PageKey) => void;
}) {
  const t = useT();
  const { lang } = useLanguage();
  return (
    <article className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold tracking-tight text-slate-900">
        {title}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
        {text}
      </p>
      {to && setPage && (
        <a
          href={pathFor(to, lang)}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            setPage(to);
          }}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 transition hover:text-sky-800"
        >
          {t("Learn more")}{" "}
          <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </a>
      )}
    </article>
  );
}

function CtaButton({
  children,
  onClick,
  variant = "primary",
  ctaName,
  page,
  to,
  setPage,
  className = "",
  type = "button",
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "white";
  ctaName?: string;
  page?: PageKey;
  /** Destination page. When set (with `setPage`), renders a real crawlable
   *  `<a href>` instead of a `<button>` — see PageLink for the same pattern. */
  to?: PageKey;
  setPage?: (page: PageKey) => void;
  className?: string;
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  const { lang } = useLanguage();
  const base = "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2";
  const variants = {
    primary: "bg-sky-600 text-white shadow-sm hover:bg-sky-700",
    secondary: "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
    white: "bg-white text-sky-700 shadow-sm hover:bg-sky-50",
  };
  const cls = `${base} ${variants[variant]} ${className} ${
    disabled ? "cursor-not-allowed opacity-60" : ""
  }`;

  if (to && setPage) {
    return (
      <a
        href={pathFor(to, lang)}
        data-cta={ctaName}
        data-page={page}
        className={cls}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onClick?.();
          setPage(to);
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-cta={ctaName}
      data-page={page}
      className={cls}
    >
      {children}
    </button>
  );
}

/**
 * Real, crawlable internal link that still performs the SPA client-side
 * transition on a plain left-click (progressive enhancement, same pattern as
 * LanguageSwitcher above). Use this instead of `<button onClick={() =>
 * setPage(x)}>` for any internal navigation — a <button> has no href, so
 * crawlers (and users who middle-click / open-in-new-tab) can't follow it.
 */
function PageLink({
  to,
  setPage,
  onClick,
  className,
  children,
  ...rest
}: {
  to: PageKey;
  setPage: (page: PageKey) => void;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick">) {
  const { lang } = useLanguage();
  return (
    <a
      href={pathFor(to, lang)}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onClick?.();
        setPage(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

// =========================================================================
// LANGUAGE SWITCHER
// =========================================================================

/**
 * EN/ES toggle. Rendered as real anchors pointing at the other language's URL
 * for this page — so the links are crawlable and open correctly in a new tab —
 * while a plain left-click is intercepted for client-side navigation.
 */
function LanguageSwitcher({
  page,
  className = "",
}: {
  page: PageKey;
  className?: string;
}) {
  const { lang, setLang, t } = useLanguage();

  return (
    <div
      role="group"
      aria-label={t("Switch language")}
      className={`flex items-center gap-0.5 rounded-lg border border-slate-200 p-0.5 ${className}`}
    >
      <Languages className="ml-1.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
      {LANGS.map((code) => {
        const active = code === lang;
        return (
          <a
            key={code}
            href={pathFor(page, code)}
            hrefLang={code}
            aria-current={active ? "true" : undefined}
            aria-label={
              code === "es"
                ? t("View this page in Spanish")
                : t("View this page in English")
            }
            onClick={(e) => {
              // Leave modified clicks (new tab / new window) to the browser.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              setLang(code);
            }}
            className={`rounded-md px-2 py-1 text-xs font-semibold uppercase transition ${
              active
                ? "bg-sky-600 text-white shadow-sm"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            {code}
          </a>
        );
      })}
    </div>
  );
}

// =========================================================================
// HEADER
// =========================================================================

function Header({ setPage, page, mobileOpen, setMobileOpen }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  // Services dropdown: opens on click AND on hover-intent; closes on Escape,
  // outside click, navigation, or unmount. Keyboard: Enter/Space toggles,
  // ArrowDown enters the menu, Escape closes and returns focus.
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const servicesButtonRef = useRef<HTMLButtonElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const t = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Drop pending hover-intent timers on unmount.
  useEffect(() => {
    return () => {
      if (openTimer.current) window.clearTimeout(openTimer.current);
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    };
  }, []);

  // Outside click closes the dropdown.
  useEffect(() => {
    if (!servicesOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (
        servicesRef.current &&
        !servicesRef.current.contains(e.target as Node)
      ) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [servicesOpen]);

  // Hover intent: open shortly after entering, close after a short grace
  // period on leave so a diagonal mouse path across the trigger doesn't
  // flicker the menu open and closed.
  const openServicesSoon = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (openTimer.current) window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(() => setServicesOpen(true), 80);
  };
  const closeServicesSoon = () => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setServicesOpen(false), 150);
  };
  const closeServices = () => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setServicesOpen(false);
  };
  const focusFirstMenuItem = () => {
    window.setTimeout(() => {
      document
        .getElementById("header-services-menu")
        ?.querySelector<HTMLElement>("a")
        ?.focus();
    }, 0);
  };

  const navLinkCls =
    "rounded-lg px-3.5 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2";

  // Services listed in the dropdown — the six pages customers actually ask for.
  const serviceMenuItems: Array<{ key: PageKey; label: string }> = [
    { key: "managed-it-services", label: "Managed IT" },
    { key: "business-wifi", label: "Business Wi-Fi" },
    { key: "unifi-installer", label: "UniFi" },
    { key: "camera-installation", label: "Security Cameras" },
    { key: "structured-cabling", label: "Structured Cabling" },
    { key: "network-installation", label: "Network Installation" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-white/95 backdrop-blur transition-shadow ${
        scrolled ? "border-slate-200 shadow-sm" : "border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        {/* Logo */}
        <PageLink to="home" setPage={setPage} aria-label={t("SightLine Networks home")} className="shrink-0">
          <img
            src={`${process.env.PUBLIC_URL}/sightline-logo-white.png`}
            alt="SightLine Networks"
            className="h-20 w-auto"
          />
        </PageLink>

        {/* Desktop nav */}
        <nav aria-label={t("Primary navigation")} className="hidden items-center gap-1 lg:flex">
          <PageLink
            to="home"
            setPage={setPage}
            className={`rounded-lg px-3.5 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 ${
              page === "home"
                ? "bg-slate-100 text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            {t("Home")}
          </PageLink>

          {/* Services dropdown */}
          <div
            ref={servicesRef}
            className="relative"
            onMouseEnter={openServicesSoon}
            onMouseLeave={closeServicesSoon}
          >
            <button
              ref={servicesButtonRef}
              aria-expanded={servicesOpen}
              aria-controls="header-services-menu"
              onClick={() => (servicesOpen ? closeServices() : setServicesOpen(true))}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  if (!servicesOpen) setServicesOpen(true);
                  focusFirstMenuItem();
                } else if (e.key === "Escape" && servicesOpen) {
                  closeServices();
                }
              }}
              className={`flex items-center gap-1 rounded-lg px-3.5 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 ${
                servicesOpen
                  ? "bg-slate-100 text-slate-900"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              {t("Services")}
              <ChevronDown
                className={`h-4 w-4 transition ${servicesOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>

            {servicesOpen && (
              <div
                id="header-services-menu"
                aria-label={t("Services")}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    closeServices();
                    servicesButtonRef.current?.focus();
                  }
                }}
                onMouseLeave={closeServicesSoon}
                className="absolute left-0 top-full z-50 w-60 pt-2"
              >
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-lg">
                  {/* Disclosure pattern: items are plain buttons reached with
                      Tab (ArrowDown on the trigger jumps to the first one), so
                      no role="menu" keyboard contract applies. */}
                  {serviceMenuItems.map(({ key, label }) => (
                    <PageLink
                      key={key}
                      to={key}
                      setPage={setPage}
                      onClick={closeServices}
                      className="flex w-full items-center rounded-lg px-3 py-2.5 text-left text-sm text-slate-700 transition hover:bg-sky-50 hover:text-sky-700 focus-visible:bg-sky-50 focus-visible:text-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                    >
                      {t(label)}
                    </PageLink>
                  ))}
                  <div className="my-1.5 border-t border-slate-100" />
                  <PageLink
                    to="services"
                    setPage={setPage}
                    onClick={closeServices}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium text-sky-700 transition hover:bg-sky-50 focus-visible:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  >
                    {t("All services")}
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </PageLink>
                </div>
              </div>
            )}
          </div>

          <PageLink
            to="areas"
            setPage={setPage}
            className={`rounded-lg px-3.5 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 ${
              page === "areas"
                ? "bg-slate-100 text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            {t("Areas")}
          </PageLink>
          <PageLink
            to="about"
            setPage={setPage}
            className={`rounded-lg px-3.5 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 ${
              page === "about"
                ? "bg-slate-100 text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            {t("About")}
          </PageLink>
          <PageLink
            to="contact"
            setPage={setPage}
            className={`rounded-lg px-3.5 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 ${
              page === "contact"
                ? "bg-slate-100 text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            {t("Contact")}
          </PageLink>

          {/* Hosting is a separate product on its own domain — keep it as an
              external link. */}
          <a
            href="https://host.sightlinenetworks.com"
            className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
          >
            {t("Hosting")}
          </a>
        </nav>

        {/* Desktop CTA cluster */}
        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher page={page} />
          <a
            href={PHONE_HREF}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-600 transition hover:text-slate-900"
            onClick={() => trackCta("phone_click", "header_phone", page)}
          >
            <Phone className="h-4 w-4" />
            {PHONE}
          </a>
          <CtaButton
            to="contact"
            setPage={setPage}
            onClick={() => trackCta("quote_click", "header_cta", page)}
            ctaName="header-quote"
            page={page}
          >
            {t("Get a Quote")}
          </CtaButton>
        </div>

        {/* Mobile: language switcher stays outside the menu so Spanish
            speakers can switch without opening the nav first. */}
        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher page={page} />
          <button
            className="rounded-lg border border-slate-200 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? t("Close menu") : t("Open menu")}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu — services listed flat, no nested drawer. */}
      {mobileOpen && (
        <div className="border-t border-slate-200 bg-white lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-6 py-4" aria-label={t("Mobile navigation")}>
            <PageLink
              to="home"
              setPage={setPage}
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              {t("Home")}
            </PageLink>

            <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              {t("Services")}
            </div>
            {serviceMenuItems.map(({ key, label }) => (
              <PageLink
                key={key}
                to={key}
                setPage={setPage}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-left text-sm text-slate-600 transition hover:bg-slate-50"
              >
                {t(label)}
              </PageLink>
            ))}
            <PageLink
              to="services"
              setPage={setPage}
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-sky-700 transition hover:bg-slate-50"
            >
              {t("All services")}
            </PageLink>

            <div className="mt-2 flex flex-col border-t border-slate-100 pt-2">
              <a
                href="https://host.sightlinenetworks.com"
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                {t("Hosting")}
              </a>
              <PageLink
                to="areas"
                setPage={setPage}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                {t("Areas")}
              </PageLink>
              <PageLink
                to="about"
                setPage={setPage}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                {t("About")}
              </PageLink>
              <PageLink
                to="contact"
                setPage={setPage}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                {t("Contact")}
              </PageLink>
            </div>

            <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3">
              <a
                href={PHONE_HREF}
                className="flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                onClick={() => trackCta("phone_click", "mobile_menu_phone", page)}
              >
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
              <CtaButton
                to="contact"
                setPage={setPage}
                onClick={() => {
                  trackCta("quote_click", "mobile_cta", page);
                  setMobileOpen(false);
                }}
                ctaName="mobile-quote"
                page={page}
                className="w-full"
              >
                {t("Get a Quote")}
              </CtaButton>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
// =========================================================================
// FOOTER
// =========================================================================

function Footer({
  setPage,
  page,
}: {
  setPage: (page: PageKey) => void;
  page: PageKey;
}) {
  const t = useT();
  const servicesLinks: Array<{ key: PageKey; label: string }> = [
    { key: "managed-it-services", label: "Managed IT Support" },
    { key: "business-wifi", label: "Business Wi-Fi" },
    { key: "unifi-installer", label: "UniFi Installation" },
    { key: "camera-installation", label: "Camera Installation" },
    { key: "security-cameras", label: "Security Cameras" },
    { key: "structured-cabling", label: "Structured Cabling" },
    { key: "network-installation", label: "Network Installation" },
    { key: "server-cloud", label: "Server & Cloud" },
    { key: "website-development", label: "Website Development" },
    { key: "hosting", label: "Business Web Hosting" },
  ];

  return (
    <footer className="border-t border-slate-800 bg-slate-950 text-slate-300">
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-14 lg:pb-14">
        {/* pb-24 on mobile clears the fixed MobileStickyCta bar so the legal
            links stay reachable at full scroll. */}
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <img
              src={`${process.env.PUBLIC_URL}/sightline-logo.png`}
              alt="SightLine Networks"
              className="h-20 w-auto"
            />
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              {t(
                "Managed IT services, business web hosting, network installation, security camera systems, and website development for companies across the Tampa Bay area."
              )}
            </p>
            <div className="mt-5 flex flex-col gap-2 text-sm">
              <a
                href={PHONE_HREF}
                className="flex items-center gap-2 text-slate-400 transition hover:text-white"
                onClick={() => trackCta("phone_click", "footer_phone", page)}
              >
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-2 text-slate-400 transition hover:text-white"
                onClick={() => trackCta("email_click", "footer_email", page)}
              >
                <Mail className="h-4 w-4" /> {EMAIL}
              </a>
            </div>
          </div>

          {/* Services links — core service pages plus the four dedicated
              landing pages. Heading links to the /services hub, which
              otherwise has no static entry point (the header's dropdown and
              mobile menu are both collapsed by default, so this is the only
              always-rendered link to it — matters for prerendered HTML). */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              <PageLink to="services" setPage={setPage} className="transition hover:text-white">
                {t("Services")}
              </PageLink>
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm" aria-label={t("Footer services")}>
              {servicesLinks.map(({ key, label }) => (
                <PageLink
                  key={key}
                  to={key}
                  setPage={setPage}
                  className="text-left text-slate-400 transition hover:text-white"
                >
                  {t(label)}
                </PageLink>
              ))}
            </nav>
          </div>

          {/* Company links */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              {t("Company")}
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm" aria-label={t("Footer company")}>
              <PageLink to="about" setPage={setPage} className="text-left text-slate-400 transition hover:text-white">{t("About Us")}</PageLink>
              <PageLink to="solutions" setPage={setPage} className="text-left text-slate-400 transition hover:text-white">{t("Solutions")}</PageLink>
              <PageLink to="areas" setPage={setPage} className="text-left text-slate-400 transition hover:text-white">{t("Areas Served")}</PageLink>
              <PageLink to="contact" setPage={setPage} className="text-left text-slate-400 transition hover:text-white">{t("Contact")}</PageLink>
              <PageLink to="privacy" setPage={setPage} className="text-left text-slate-400 transition hover:text-white">{t("Privacy Policy")}</PageLink>
            </nav>
          </div>

          {/* Areas served */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              {t("Areas Served")}
            </h2>
            <div className="mt-4 flex flex-col gap-2.5 text-sm text-slate-400">
              <span>Tampa</span>
              <span>Clearwater</span>
              <span>Largo</span>
              <span>St. Petersburg</span>
              <span>Pinellas Park</span>
              <span>Brandon</span>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-slate-800 pt-8 md:flex-row">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} SightLine Networks LLC.{" "}
            {t("All rights reserved. Clearwater, FL — Serving Tampa Bay and beyond.")}
          </p>
          <div className="flex gap-4 text-xs text-slate-500">
            <PageLink to="privacy" setPage={setPage} className="transition hover:text-slate-300">{t("Privacy Policy")}</PageLink>
            <PageLink
              to="privacy"
              setPage={setPage}
              className="transition hover:text-slate-300"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.setTimeout(() => {
                    const el = document.getElementById("do-not-sell");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 120);
                }
              }}
            >
              {t("Do Not Sell or Share My Personal Information")}
            </PageLink>
            <PageLink to="contact" setPage={setPage} className="transition hover:text-slate-300">{t("Terms of Service")}</PageLink>
          </div>
        </div>
      </div>
    </footer>
  );
}
// =========================================================================
// HOME PAGE
// =========================================================================

function HomePage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();

  const scrollToLeadForm = () => {
    document
      .getElementById("home-lead-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Problem cards — real pain points, each linking to the page that fixes it.
  const problemCards: Array<{
    icon: typeof Wifi;
    title: string;
    text: string;
    target: PageKey;
  }> = [
    {
      icon: Wifi,
      title: "Wi-Fi dead zones in the back office or warehouse",
      text: "Signal drops halfway across the building, calls cut out mid-sentence, and the payment terminal keeps losing its connection.",
      target: "business-wifi",
    },
    {
      icon: Zap,
      title: "Slow computers and constant IT fires",
      text: "Your team loses time every day to slow machines, glitchy software, and the same recurring problems nobody has time to fix.",
      target: "managed-it-services",
    },
    {
      icon: Cable,
      title: "A cabling mess from years of patchwork additions",
      text: "Unlabeled cables, mystery connections, and a network rack nobody wants to touch when something needs to change.",
      target: "structured-cabling",
    },
    {
      icon: Camera,
      title: "Cameras with gaps in coverage — or no remote view",
      text: "Important areas aren't covered, footage is grainy, and you can't check in when you're away from the site.",
      target: "camera-installation",
    },
    {
      icon: Phone,
      title: "An IT vendor that's hard to reach when something breaks",
      text: "Tickets disappear into a portal and the phone goes unanswered while your team sits and waits.",
      target: "contact",
    },
    {
      icon: Building2,
      title: "A growing team outgrowing its ad-hoc setup",
      text: "More people, more devices, more rooms — and a network that was never really designed for any of it.",
      target: "network-installation",
    },
  ];

  // Primary services grid — the six pages customers actually search for.
  const primaryServices: Array<{
    icon: typeof Wifi;
    title: string;
    text: string;
    page: PageKey;
  }> = [
    {
      icon: Headphones,
      title: "Managed IT",
      text: "Help desk, monitoring, patching, and on-site support from a local team — so everyday IT problems stop landing on your desk.",
      page: "managed-it-services",
    },
    {
      icon: Wifi,
      title: "Business Wi-Fi",
      text: "Business-grade Wi-Fi designed around your building, with coverage measured and verified in every area your team works.",
      page: "business-wifi",
    },
    {
      icon: Router,
      title: "UniFi Deployment",
      text: "UniFi access points, switches, gateways, and Protect cameras — installed, configured, and adopted into a controller.",
      page: "unifi-installer",
    },
    {
      icon: Camera,
      title: "Security Cameras",
      text: "IP camera systems with remote viewing on your phone, sized for offices, warehouses, and parking areas.",
      page: "camera-installation",
    },
    {
      icon: Cable,
      title: "Structured Cabling",
      text: "Cat6 and Cat6A drops, patch panels, and rack cleanups — tested, labeled, and documented so the next change is easy.",
      page: "structured-cabling",
    },
    {
      icon: Network,
      title: "Network Installation",
      text: "Switches, firewalls, access points, and cabling — a network designed for how your business actually works.",
      page: "network-installation",
    },
  ];

  return (
    // Bottom padding on mobile only, clearing the sticky call/consult bar.
    <main className="pb-24 lg:pb-0">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-b from-sky-50/60 via-white to-white">
        <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-16 lg:pb-24 lg:pt-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-sky-700">
              <MapPin className="h-3.5 w-3.5" />
              {t("Serving Tampa Bay businesses")}
            </div>

            <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Reliable IT, Networks & Security for Tampa Bay Businesses")}
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              {t(
                "Networks that stay up, Wi-Fi that reaches every corner, cameras that cover the blind spots, and IT support that answers the phone — from a local Tampa Bay team."
              )}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <CtaButton
                to="contact"
                setPage={setPage}
                onClick={() => trackCta("quote_click", "hero", "home")}
                ctaName="hero-quote"
                page="home"
              >
                {t("Get a Free Assessment")} <ArrowRight className="h-4 w-4" />
              </CtaButton>
              <a
                href={PHONE_HREF}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                onClick={() => trackCta("phone_click", "hero_phone", "home")}
              >
                <Phone className="h-4 w-4" /> {t("Call")} {PHONE}
              </a>
            </div>

            {/* Trust strip — verifiable positioning only. */}
            <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2 border-t border-slate-200 pt-5">
              {[
                "Local Tampa Bay team",
                "Clear, upfront quotes",
                "One call for IT, Wi-Fi, cabling & cameras",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2 text-sm font-medium text-slate-600"
                >
                  <Check className="h-4 w-4 shrink-0 text-sky-600" aria-hidden="true" />
                  {t(item)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Problem cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <SectionLabel>{t("Does this sound familiar?")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("The technology problems we hear about every week")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "If any of these describe your workplace, you're not stuck with them. Each one is a normal part of our week."
            )}
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {problemCards.map(({ icon: Icon, title, text, target }) => (
            <article
              key={title}
              className="relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">{t(title)}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {t(text)}
              </p>
              <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-700">
                {t("We fix this.")}{" "}
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </p>
              {/* Full-card click target: a real link overlaying the card
                  (not block content inside it) so the card is crawlable. */}
              <PageLink
                to={target}
                setPage={setPage}
                onClick={() => trackCta("services_click", title, "home")}
                className="absolute inset-0 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
              >
                <span className="sr-only">
                  {t(title)} — {t("learn more")}
                </span>
              </PageLink>
            </article>
          ))}
        </div>
      </section>

      {/* Primary services */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <SectionLabel>{t("What we do")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("One team for IT, Wi-Fi, cabling, and cameras")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t(
                "Instead of juggling separate vendors for your computers, network, cabling, and cameras, you get one contractor who owns the whole picture."
              )}
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {primaryServices.map(({ icon: Icon, title, text, page }) => (
              <FeatureCard
                key={page}
                icon={Icon}
                title={t(title)}
                text={t(text)}
                to={page}
                setPage={setPage}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Feature — Business Wi-Fi */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionLabel>{t("Business Wi-Fi & networking")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Wi-Fi that reaches every corner of the building")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t(
                "We start with a site survey — not a floor-plan guess — and design the network around how your building is built and how your team works."
              )}
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Site survey — we measure where coverage actually reaches today",
                "Design — access point and switch placement based on your space",
                "Installation — mounting, cabling, and configuration by one team",
                "After-support — monitoring, changes, and troubleshooting when you need it",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" aria-hidden="true" />
                  {t(item)}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <CtaButton
                to="business-wifi"
                setPage={setPage}
                onClick={() => trackCta("wifi_assessment_click", "home_wifi_feature", "home")}
                ctaName="home-wifi-cta"
                page="home"
              >
                {t("Request a Wi-Fi Assessment")} <ArrowRight className="h-4 w-4" />
              </CtaButton>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8">
            <h3 className="text-lg font-semibold text-slate-900">
              {t("Why it matters in Tampa Bay")}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {t(
                "The buildings around here are hard on Wi-Fi. Concrete block construction, older commercial buildings, metal racking in warehouses, and outdoor patios all eat signal. Consumer routers and mesh kits weren't built for that — or for the number of devices a business connects at once. We design coverage around the building you actually have."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Feature — Managed IT */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
            <div>
              <SectionLabel>{t("Managed IT")}</SectionLabel>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                {t("IT support that picks up the phone")}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                {t(
                  "Built for 10–100 seat offices that don't have in-house IT — and don't want to depend on a national call center, either. Your team calls us, we know your setup, and problems get handled."
                )}
              </p>
              <div className="mt-8">
                <CtaButton
                  to="managed-it-services"
                  setPage={setPage}
                  onClick={() => trackCta("managed_it_assessment_click", "home_managed_it", "home")}
                  ctaName="home-managed-it-cta"
                  page="home"
                >
                  {t("Get an IT Assessment")} <ArrowRight className="h-4 w-4" />
                </CtaButton>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h3 className="text-lg font-semibold text-slate-900">
                {t("What's included")}
              </h3>
              <ul className="mt-5 space-y-3.5">
                {[
                  "Help desk support for your team",
                  "Monitoring so problems are caught early",
                  "Patching and updates kept current",
                  "Vendor coordination — internet, phone, and software providers",
                  "On-site visits when remote support isn't enough",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700"
                  >
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" aria-hidden="true" />
                    {t(item)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <SectionLabel>{t("How we work")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("From first call to working network")}
          </h2>
        </div>

        <ol className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: Phone,
              step: "1",
              title: "Call or request a quote",
              text: "Tell us what's going on. A short conversation narrows down what you actually need.",
            },
            {
              icon: Search,
              step: "2",
              title: "Free assessment or site visit",
              text: "We look at your building and your equipment, and find out what's really causing the problem.",
            },
            {
              icon: FileText,
              step: "3",
              title: "Written quote with clear scope",
              text: "You get an itemized, plain-English quote. No vague line items, no surprises.",
            },
            {
              icon: Wrench,
              step: "4",
              title: "Installation & ongoing support",
              text: "We do the work, test it, document it, and stay available afterward.",
            },
          ].map(({ icon: Icon, step, title, text }) => (
            <li
              key={step}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {t("Step")} {step}
                </span>
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">{t(title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(text)}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Recent Projects — PLACEHOLDER structure. Real project photos and
          details are pending; the cards below are illustrative examples only
          and are visibly labeled as such (see the factual-copy rules). */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <SectionLabel>{t("Recent Projects")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("The kind of work we take on")}
            </h2>
            <p className="mt-3 text-sm italic text-slate-500">
              {t(
                "Project spotlights coming soon — real Tampa Bay project photos and details will be added here."
              )}
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Wifi, label: "Office Wi-Fi refresh — Clearwater" },
              { icon: Camera, label: "Warehouse camera coverage — Tampa" },
              { icon: Cable, label: "Rack cleanup & re-termination — St. Petersburg" },
              { icon: Headphones, label: "Managed IT handover — Brandon" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center"
              >
                <Icon className="h-7 w-7 text-slate-400" aria-hidden="true" />
                <span className="text-sm font-medium text-slate-600">{t(label)}</span>
                <span className="text-xs uppercase tracking-wide text-slate-500">
                  {t("Example project")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / local section + brands */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <SectionLabel>{t("Why SightLine Networks")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Why Tampa Bay businesses call SightLine")}
            </h2>
          </div>

          <div className="grid gap-10 lg:grid-cols-2">
            <ul className="space-y-4">
              {[
                {
                  icon: MapPin,
                  text: "Local and independent — we answer our own phone, and the people who quote your project are the ones who do the work.",
                },
                {
                  icon: Building2,
                  text: "One contractor across IT, Wi-Fi, cabling, and cameras, so nothing falls between vendors.",
                },
                {
                  icon: CheckCircle2,
                  text: "Plain-English explanations — you'll know what we're doing and why.",
                },
                {
                  icon: FileText,
                  text: "Upfront written quotes before any work starts.",
                },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm leading-relaxed text-slate-700">{t(text)}</span>
                </li>
              ))}
            </ul>

            {/* Reviews widget — structure only. No fabricated review text;
                connect a live reviews widget here. */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">
                {t("What our customers say")}
              </h3>
              <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6">
                <p className="text-sm italic text-slate-500">
                  {t("Google reviews widget placeholder — connect live reviews here.")}
                </p>
              </div>
            </div>
          </div>

          {/* Brands strip — reuses the site's existing brands markup pattern.
              Neutral "we install" phrasing only; no partner/certification
              claims are made here. */}
          <div className="mt-16 border-t border-slate-200 pt-12">
            <div className="mb-10 max-w-3xl">
              <SectionLabel>{t("Brands we install")}</SectionLabel>
              <h3 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t("UniFi, Cisco, Synology — equipment we install every week")}
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed">
                {t(
                  "SightLine Networks designs, installs, and supports business systems built on Ubiquiti UniFi (UniFi Network, UniFi Protect, and UniFi Access), Cisco networking and VOIP equipment, and Synology NAS for local backup and storage. We also install IP security cameras from Hikvision, Axis, and Dahua, plus VOIP phone systems from Yealink, Poly, and Grandstream."
                )}
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { name: "Ubiquiti UniFi", category: "UniFi Network, UniFi Protect & UniFi Access — cameras, networking & security", logoUrl: `${process.env.PUBLIC_URL}/brands/unifi.png` },
                { name: "Cisco", category: "Cisco & Meraki networking, switches and VOIP" },
                { name: "Synology", category: "Synology NAS — local storage, backup & DSM" },
                { name: "Hikvision", category: "IP security cameras & NVR systems", logoUrl: `${process.env.PUBLIC_URL}/brands/hikvision.png` },
                { name: "Axis Communications", category: "Enterprise IP cameras", logoUrl: `${process.env.PUBLIC_URL}/brands/axis.png` },
                { name: "Dahua", category: "IP cameras & surveillance", logoUrl: `${process.env.PUBLIC_URL}/brands/dahua.png` },
                { name: "Yealink", category: "VOIP desk phones & conference phones" },
                { name: "Poly (Polycom)", category: "VOIP & video conferencing" },
                { name: "Grandstream", category: "VOIP phones, gateways & PBX" },
              ].map(({ name, category, logoUrl }) => (
                <div
                  key={name}
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-sky-300 hover:shadow-md"
                >
                  <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-xl bg-slate-50 px-3">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={`${name} logo`}
                        className="h-10 w-auto max-w-[80px] object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-base font-bold tracking-tight text-slate-700">
                        {name}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="text-base font-semibold text-slate-900">{name}</div>
                    <div className="mt-0.5 text-sm text-slate-600">{t(category)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center">
          <SectionLabel>{t("Service area")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Serving businesses throughout Tampa Bay")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600 leading-relaxed">
            {t(
              "We work across Pinellas and Hillsborough counties — Tampa, Clearwater, St. Petersburg, Largo, Pinellas Park, Brandon, and the communities between them."
            )}
          </p>
          <div className="mt-8">
            <ServiceArea
              cities={["Tampa", "Clearwater", "St. Petersburg", "Largo", "Pinellas Park", "Brandon"]}
            />
          </div>
          <PageLink
            to="areas"
            setPage={setPage}
            className="mt-8 inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
          >
            {t("See all areas we serve")}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </PageLink>
        </div>
      </section>

      {/* FAQ — the accordion renders HOME_FAQS; buildSchema() emits the
          matching FAQPage JSON-LD from the same array. */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <SectionLabel>{t("Frequently asked questions")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Common questions from Tampa Bay businesses")}
            </h2>
          </div>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {HOME_FAQS.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-900 marker:hidden">
                  <span>{t(q)}</span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA + lead form */}
      <section className="bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 pb-20 pt-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t("Ready to get your network, IT, and cameras sorted?")}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sky-50">
              {t(
                "Tell us what's not working. We'll take a look and give you a clear, written quote — no obligation."
              )}
            </p>
            <a
              href={PHONE_HREF}
              className="mt-6 inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
              onClick={() => trackCta("phone_click", "home_bottom_phone", "home")}
            >
              <Phone className="h-4 w-4" /> {t("Call")} {PHONE}
            </a>
          </div>

          <div
            id="home-lead-form"
            className="mx-auto mt-12 max-w-3xl scroll-mt-24 rounded-2xl bg-white p-6 shadow-lg sm:p-8"
          >
            <h3 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("Get a Free Assessment")}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              {t("Fill out the form and our Tampa Bay team will get back to you.")}
            </p>
            <div className="mt-6">
              <LeadForm
                page="home"
                source="main-website"
                formId="home-lead-form"
                subject="Free Assessment Request"
                submitLabel="Get My Free Assessment"
              />
            </div>
          </div>
        </div>
      </section>

      <MobileStickyCta
        onConsultClick={() => {
          trackCta("quote_click", "home_sticky", "home");
          scrollToLeadForm();
        }}
        ctaName="home"
        page="home"
      />
    </main>
  );
}
// =========================================================================
// SERVICES PAGE
// =========================================================================

function ServicesPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  // Copy is stored in English and translated at render time — the English
  // string doubles as the dictionary key (see src/i18n/es.ts).
  const services: Array<{ icon: typeof Headphones; title: string; text: string; features: string[]; page: PageKey }> = [
    {
      icon: Headphones,
      title: "Managed IT Support",
      text: "Proactive monitoring, helpdesk support, patch management, and strategic technology guidance. We keep your systems running so your team stays productive.",
      features: ["24/7 monitoring", "Remote & on-site support", "Vendor management", "Technology roadmaps"],
      page: "managed-it-services",
    },
    {
      icon: Globe,
      title: "Network Installation",
      text: "Professional network design and deployment including structured cabling, switches, firewalls, access points, and enterprise-grade Wi-Fi coverage.",
      features: ["Structured cabling", "Wireless design", "Firewall setup", "Network audits"],
      page: "network-installation",
    },
    {
      icon: Camera,
      title: "Security Camera Systems",
      text: "Commercial surveillance system design, installation, and support. HD and 4K cameras with remote viewing, cloud storage, and motion-triggered alerts.",
      features: ["HD/4K cameras", "Remote viewing", "Cloud recording", "Access control"],
      page: "security-cameras",
    },
    {
      icon: Server,
      title: "Server & Cloud Infrastructure",
      text: "On-premise server installation, cloud migration, hybrid environments, and disaster recovery planning for businesses that depend on data availability.",
      features: ["Server setup", "Cloud migration", "Backup & recovery", "Virtualization"],
      page: "server-cloud",
    },
    {
      icon: Monitor,
      title: "Website Development",
      text: "Modern, responsive business websites designed for performance, search visibility, and lead generation. Built to work across all devices with ongoing support available.",
      features: ["Responsive design", "SEO foundations", "Lead capture", "CMS management"],
      page: "website-development",
    },
    {
      icon: Lock,
      title: "Business Web Hosting",
      text: "Managed hosting plans built for company websites with free SSL, daily automated backups, uptime monitoring, and expert support when you need it.",
      features: ["Free SSL", "Daily backups", "99.9% uptime", "Managed support"],
      page: "hosting",
    },
    {
      icon: LineChart,
      title: "IT Consulting & Strategy",
      text: "Technology assessments, infrastructure planning, compliance guidance, and strategic IT roadmaps for businesses preparing for growth or change.",
      features: ["IT assessments", "Compliance guidance", "Vendor evaluation", "Budget planning"],
      page: "it-consulting",
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("Our services")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Business IT services for Tampa Bay companies")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "From managed IT support and network installation to security cameras, hosting, and website development — we provide the technology services your business needs to run smoothly and grow confidently."
              )}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaButton
                to="contact"
                setPage={setPage}
                onClick={() => trackCta("quote_click", "services_hero", "services")}
                ctaName="services-quote"
                page="services"
              >
                {t("Request a Quote")}
              </CtaButton>
              <CtaButton
                variant="secondary"
                to="hosting"
                setPage={setPage}
              >
                {t("View Hosting Plans")}
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* Service cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {services.map((svc) => (
            <article
              key={svc.title}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                <svc.icon className="h-5 w-5" />
              </div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">
                {t(svc.title)}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {t(svc.text)}
              </p>
              <ul className="mt-4 grid grid-cols-2 gap-2">
                {svc.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-1.5 text-xs text-slate-500"
                  >
                    <Check className="h-3.5 w-3.5 text-sky-500" /> {t(f)}
                  </li>
                ))}
              </ul>
              <PageLink
                to={svc.page}
                setPage={setPage}
                data-cta="service-detail"
                data-service={svc.title}
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 transition hover:text-sky-800"
              >
                {svc.title === "Business Web Hosting"
                  ? t("View plans")
                  : t("Learn more")}{" "}
                <ChevronRight className="h-4 w-4" />
              </PageLink>
            </article>
          ))}
        </div>
      </section>

      {/* Cross-link CTA */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t("Not sure which service you need?")}
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                {t(
                  "Our solutions page helps you find the right service based on the business problem you are trying to solve. Or talk to our team directly for a personalized recommendation."
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <CtaButton to="solutions" setPage={setPage}>
                {t("Browse Solutions")}
              </CtaButton>
              <CtaButton
                variant="secondary"
                to="contact"
                setPage={setPage}
                onClick={() => trackCta("quote_click", "services_bottom", "services")}
                ctaName="services-bottom-quote"
                page="services"
              >
                {t("Talk to Our Team")}
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// HOSTING PAGE
// =========================================================================

function HostingPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  const [billing, setBilling] = useState<"monthly" | "annual">("annual");

  const plans = [
    {
      name: "Starter",
      monthlyPrice: 29,
      annualPrice: 290,
      annualMonthly: 24,
      description: "For small business websites, landing pages, and brochure sites that need reliable uptime and basic support.",
      features: [
        "1 website",
        "10 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Uptime monitoring",
        "Email support",
        "99.9% uptime SLA",
      ],
      featured: false,
    },
    {
      name: "Business",
      monthlyPrice: 59,
      annualPrice: 590,
      annualMonthly: 49,
      description: "For growing business websites that need faster performance, priority support, and room to scale.",
      features: [
        "Up to 3 websites",
        "25 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Uptime & performance monitoring",
        "Priority phone & email support",
        "Staging environment",
        "99.9% uptime SLA",
      ],
      featured: true,
    },
    {
      name: "Pro",
      monthlyPrice: 99,
      annualPrice: 990,
      annualMonthly: 83,
      description: "For high-traffic websites, custom applications, and businesses that need maximum performance and support.",
      features: [
        "Up to 10 websites",
        "50 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Advanced performance monitoring",
        "Dedicated support manager",
        "Staging environment",
        "Disaster recovery",
        "99.99% uptime SLA",
      ],
      featured: false,
    },
  ];

  const faqItems = [
    {
      q: "What types of websites can I host?",
      a: "Our plans support WordPress sites, custom-built business websites, landing pages, e-commerce stores, and web applications. We optimize for the platform you use.",
    },
    {
      q: "Is SSL included with all plans?",
      a: "Yes. Every hosting plan includes a free SSL certificate that auto-renews, ensuring your site is always served over HTTPS for visitor trust and SEO.",
    },
    {
      q: "How do backups work?",
      a: "We run daily automated backups of your website files and databases. Business and Pro plans include one-click restore. Backups are stored off-server for safety.",
    },
    {
      q: "Can I bundle hosting with website development?",
      a: "Absolutely. Many of our clients pair a website build with ongoing hosting and managed support. We offer package pricing when you combine services.",
    },
    {
      q: "What happens if my site goes down?",
      a: "Our monitoring systems detect downtime within minutes and our team begins remediation immediately. Business and Pro plans include proactive alerting and faster response.",
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-sky-50/50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center lg:py-20">
          <SectionLabel>{t("Business web hosting")}</SectionLabel>
          <h1 className="mx-auto mt-3 max-w-3xl text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            {t("Fast, secure hosting with managed support for your business website")}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
            {t(
              "Every plan includes free SSL, daily backups, uptime monitoring, and hands-on support from our Tampa Bay team. Pick a plan and launch with confidence."
            )}
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              onClick={() => setBilling("monthly")}
              className={`rounded-lg px-5 py-2.5 text-sm font-medium transition ${
                billing === "monthly"
                  ? "bg-sky-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t("Monthly")}
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`rounded-lg px-5 py-2.5 text-sm font-medium transition ${
                billing === "annual"
                  ? "bg-sky-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t("Annual")}{" "}
              <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                billing === "annual" ? "bg-sky-500 text-white" : "bg-emerald-50 text-emerald-700"
              }`}>
                {t("Save 17%")}
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="mx-auto max-w-7xl px-6 -mt-2 pb-20">
        <div className="grid gap-6 lg:grid-cols-3" aria-label={t("View Hosting Plans")}>
          {plans.map((plan) => {
            const price = billing === "annual" ? plan.annualMonthly : plan.monthlyPrice;
            const total = billing === "annual" ? plan.annualPrice : null;
            return (
              <article
                key={plan.name}
                className={`relative flex flex-col rounded-2xl border p-7 shadow-sm transition hover:shadow-md ${
                  plan.featured
                    ? "border-sky-300 bg-white shadow-sky-100/50 ring-1 ring-sky-200"
                    : "border-slate-200 bg-white"
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-6 rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                    {t("Most Popular")}
                  </div>
                )}
                <h2 className="text-xl font-bold text-slate-900">{plan.name}</h2>
                <p className="mt-2 text-sm text-slate-500">{t(plan.description)}</p>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tight text-slate-900">
                    ${price}
                  </span>
                  <span className="text-sm text-slate-500">{t("/mo")}</span>
                </div>
                {total && (
                  <p className="mt-1 text-xs text-slate-400">
                    ${total}/{t("year")} {t("billed annually")}
                  </p>
                )}

                <CtaButton
                  to="contact"
                  setPage={setPage}
                  onClick={() => trackCta("hosting_plan_click", plan.name, "hosting")}
                  ctaName={`hosting-${plan.name.toLowerCase()}`}
                  page="hosting"
                  className={`mt-6 w-full ${plan.featured ? "" : ""}`}
                  variant={plan.featured ? "primary" : "secondary"}
                >
                  {t("Choose")} {plan.name}
                </CtaButton>

                <ul className="mt-6 flex-1 space-y-3 border-t border-slate-100 pt-6">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-slate-600"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                      {t(f)}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {/* Guarantee */}
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-slate-500">
          <Shield className="h-5 w-5 text-sky-600" />
          <span>{t("30-day money-back guarantee on all plans. No long-term contracts required.")}</span>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">
              {t("Everything included with your hosting")}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-slate-600">
              {t("Enterprise-grade infrastructure and hands-on management for every plan.")}
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Lock, title: "Free SSL Certificate", text: "Auto-renewing HTTPS encryption for every website on your plan." },
              { icon: HardDrive, title: "Daily Backups", text: "Automated daily backups stored off-server with easy restore options." },
              { icon: Zap, title: "Performance Optimized", text: "SSD storage, server-level caching, and CDN-ready infrastructure." },
              { icon: Headphones, title: "Expert Support", text: "Real people who understand your hosting, available when you need them." },
              { icon: Shield, title: "Security Monitoring", text: "Firewall protection, malware scanning, and proactive threat detection." },
              { icon: Globe, title: "99.9% Uptime SLA", text: "Guaranteed availability backed by infrastructure redundancy." },
              { icon: Wrench, title: "Managed Updates", text: "We handle CMS, plugin, and server updates so your site stays current." },
              { icon: ArrowRight, title: "Free Migration", text: "We move your existing website to our platform at no extra cost." },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <Icon className="mb-3 h-5 w-5 text-sky-600" />
                <h3 className="text-sm font-semibold text-slate-900">{t(title)}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{t(text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[0.4fr_0.6fr]">
          <div>
            <SectionLabel>{t("Hosting FAQ")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
              {t("Common questions about our hosting")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("Can't find what you're looking for? Our team is happy to answer any hosting questions.")}
            </p>
            <CtaButton
              to="contact"
              setPage={setPage}
              onClick={() => trackCta("quote_click", "hosting_faq_cta", "hosting")}
              className="mt-6"
              ctaName="hosting-faq-contact"
              page="hosting"
            >
              {t("Ask a Question")}
            </CtaButton>
          </div>

          <div className="space-y-6">
            {faqItems.map(({ q, a }) => (
              <div key={q} className="border-b border-slate-200 pb-6 last:border-0">
                <h3 className="text-base font-semibold text-slate-900">{t(q)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(a)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-slate-200 bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 py-14 text-center">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            {t("Ready to host your business website with confidence?")}
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-50">
            {t(
              "Choose a plan above or talk to our team about custom hosting needs. We also bundle hosting with website development."
            )}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              to="contact"
              setPage={setPage}
              onClick={() => trackCta("quote_click", "hosting_bottom", "hosting")}
              ctaName="hosting-bottom-quote"
              page="hosting"
            >
              {t("Talk to Our Team")}
            </CtaButton>
            <CtaButton
              variant="secondary"
              to="services"
              setPage={setPage}
              className="border-sky-500 bg-transparent text-white hover:bg-sky-700"
            >
              {t("View All Services")}
            </CtaButton>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// REUSABLE SERVICE PAGE TEMPLATE
// =========================================================================

function ServicePageTemplate({
  setPage,
  icon: Icon,
  title,
  serviceName,
  subtitle,
  intro,
  features,
  benefits,
  relatedServices,
  faq,
  brands,
}: {
  setPage: (page: PageKey) => void;
  icon: typeof Globe;
  /** Long, keyword-rich H1. English source string; translated at render. */
  title: string;
  /**
   * Short lowercase noun phrase for mid-sentence use ("network installation").
   * Kept separate from `title` because the H1 is too long to read well inside
   * a sentence, and because Spanish needs the article baked into the phrase
   * ("la instalación de redes") for these headings to be grammatical.
   */
  serviceName: string;
  subtitle: string;
  intro: string;
  features: Array<{ title: string; text: string }>;
  benefits: string[];
  relatedServices: Array<{ label: string; page: PageKey }>;
  faq: Array<{ q: string; a: string }>;
  brands?: Array<{ name: string; logoUrl?: string }>;
}) {
  const t = useT();
  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-sky-50/50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
              <Icon className="h-6 w-6" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t(title)}
            </h1>
            <p className="mt-2 text-lg font-medium text-sky-600">{t(subtitle)}</p>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">{t(intro)}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaButton
                to="contact"
                setPage={setPage}
                onClick={() => trackCta("quote_click", `${title}_hero`, "services")}
                ctaName={`service-${title}-quote`}
                page="services"
              >
                {t("Get a Free Quote")} <ArrowRight className="h-4 w-4" />
              </CtaButton>
              <CtaButton variant="secondary" to="services" setPage={setPage}>
                {t("View All Services")}
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <SectionLabel>{t("What's included")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            {t("How we deliver")} {t(serviceName)}
          </h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">{t(f.title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(f.text)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Brands */}
      {brands && brands.length > 0 && (
        <section className="border-b border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-7xl px-6 py-14">
            <div className="mb-8 text-center">
              <SectionLabel>{t("Brands we work with")}</SectionLabel>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t("Trusted brands we install and support")}
              </h2>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              {brands.map((brand) => (
                <div
                  key={brand.name}
                  className="flex h-20 w-36 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 shadow-sm transition hover:shadow-md"
                >
                  {brand.logoUrl ? (
                    <img
                      src={brand.logoUrl}
                      alt={`${brand.name} logo`}
                      className="h-10 w-auto max-w-[120px] object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-center text-sm font-bold tracking-tight text-slate-700">
                      {brand.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t("Why businesses choose SightLine for")} {t(serviceName)}
              </h2>
              <ul className="mt-6 space-y-3">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-500" />
                    <span>{t(b)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">{t("Related services")}</h3>
              <p className="mt-2 text-sm text-slate-500">{t("Explore other services that pair well.")}</p>
              <div className="mt-4 flex flex-col gap-2">
                {relatedServices.map((rs) => (
                  <PageLink
                    key={rs.label}
                    to={rs.page}
                    setPage={setPage}
                    className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  >
                    {t(rs.label)}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </PageLink>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20">
          <div className="grid gap-12 lg:grid-cols-[0.4fr_0.6fr]">
            <div>
              <SectionLabel>{t("FAQ")}</SectionLabel>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
                {t("Common questions")}
              </h2>
              <CtaButton
                to="contact"
                setPage={setPage}
                onClick={() => trackCta("quote_click", `${title}_faq`, "services")}
                className="mt-6"
              >
                {t("Ask a Question")}
              </CtaButton>
            </div>
            <div className="space-y-6">
              {faq.map(({ q, a }) => (
                <div key={q} className="border-b border-slate-200 pb-6 last:border-0">
                  <h3 className="text-base font-semibold text-slate-900">{t(q)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(a)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-slate-200 bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 py-14 text-center">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            {t("Ready to get started with")} {t(serviceName)}?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-50">
            {t("Tell us about your project and get a free quote from our Tampa Bay team.")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              to="contact"
              setPage={setPage}
              onClick={() => trackCta("quote_click", `${title}_bottom`, "services")}
            >
              {t("Get a Free Quote")}
            </CtaButton>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
            >
              <Phone className="h-4 w-4" /> {t("Call")} {PHONE}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// INDIVIDUAL SERVICE PAGES
// =========================================================================

function ManagedItPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Headphones}
      title="Managed IT Support"
      serviceName="managed IT support"
      subtitle="Proactive IT management for Tampa Bay businesses"
      intro="Stop putting out IT fires. Our managed IT service provides 24/7 monitoring, helpdesk support, cybersecurity, patch management, and strategic technology guidance — so your team stays productive and your systems stay secure."
      features={[
        { title: "24/7 Monitoring & Alerting", text: "We monitor your servers, network, and endpoints around the clock and respond to issues before they affect your operations." },
        { title: "Helpdesk Support", text: "Your team gets fast access to experienced technicians for troubleshooting, how-to questions, and technical guidance via phone, email, or remote session." },
        { title: "Patch Management", text: "We keep your operating systems, applications, and firmware up to date with tested patches that reduce vulnerabilities without disrupting work." },
        { title: "Cybersecurity", text: "Endpoint protection, email filtering, DNS security, and firewall management to defend your business against ransomware, phishing, and data breaches." },
        { title: "Vendor Management", text: "We coordinate with your ISP, software vendors, and hardware suppliers so you have a single point of contact for all technology issues." },
        { title: "Technology Roadmaps", text: "Quarterly business reviews with strategic recommendations on upgrades, budgets, and IT investments aligned to your business goals." },
      ]}
      benefits={[
        "Reduce downtime with proactive monitoring and faster issue resolution",
        "Predictable monthly costs instead of expensive break-fix surprises",
        "Local Tampa Bay team available for on-site support when needed",
        "Certified engineers with Cisco, Microsoft, and VMware expertise",
        "Single point of contact for all your technology needs",
        "Scalable support that grows with your business",
      ]}
      relatedServices={[
        { label: "Network Installation", page: "network-installation" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Security Cameras", page: "security-cameras" },
        { label: "IT Consulting & Strategy", page: "it-consulting" },
      ]}
      faq={[
        { q: "What does managed IT include?", a: "Our managed IT service covers 24/7 monitoring, helpdesk support, patch management, cybersecurity, vendor coordination, and strategic technology planning. We tailor the service to your business needs." },
        { q: "How fast is your response time?", a: "Critical issues receive immediate attention. Standard support requests are typically addressed within one hour during business hours, with 24/7 emergency coverage available." },
        { q: "Do you offer on-site support?", a: "Yes. While most issues are resolved remotely, we provide on-site support throughout the Tampa Bay area whenever hands-on work is needed." },
        { q: "Can you take over from our current IT provider?", a: "Absolutely. We handle the full onboarding process including documentation, asset inventory, credential transfer, and a transition plan to minimize disruption." },
      ]}
    />
  );
}

function NetworkInstallationPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Globe}
      title="Ubiquiti UniFi & Cisco Network Installation in Tampa Bay"
      serviceName="network installation"
      subtitle="Certified UniFi and Cisco installers for business networks"
      intro="Whether you're building out a new office, upgrading aging infrastructure, or expanding to a new location, we design and install Ubiquiti UniFi and Cisco networks that are fast, reliable, and built to grow with your business — including UniFi access points, Cisco/Meraki switches, firewalls, and structured cabling."
      features={[
        { title: "Structured Cabling", text: "Cat6/Cat6A cabling runs, cable management, patch panels, and rack installations built to commercial standards with full documentation." },
        { title: "Enterprise Wi-Fi Design", text: "Site surveys, heat mapping, and access point deployment for seamless wireless coverage across your entire facility without dead zones." },
        { title: "Switches & Firewalls", text: "Managed switches, VLANs, QoS configuration, and next-gen firewall deployment for secure, segmented network infrastructure." },
        { title: "Network Audits", text: "Comprehensive assessment of your existing infrastructure to identify bottlenecks, security gaps, and upgrade opportunities." },
        { title: "VPN & Remote Access", text: "Secure site-to-site and remote access VPN configuration so your team can work from anywhere without compromising security." },
        { title: "Documentation & Training", text: "Complete network diagrams, port maps, credential documentation, and training for your team on day-to-day management." },
      ]}
      benefits={[
        "Professionally designed and installed by certified network engineers",
        "Clean, documented cabling that passes inspection and lasts for years",
        "Enterprise-grade Wi-Fi with complete coverage and zero dead zones",
        "Scalable design that accommodates business growth without rework",
        "Full documentation and network diagrams provided at handoff",
        "Ongoing support available through our managed IT service",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it-services" },
        { label: "Security Cameras", page: "security-cameras" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Business Web Hosting", page: "hosting" },
      ]}
      faq={[
        { q: "Can you install cabling in an existing building?", a: "Yes. We install structured cabling in new and existing spaces, including retrofit projects. We work around your schedule to minimize disruption to daily operations." },
        { q: "What brands do you work with?", a: "We deploy equipment from Cisco, Ubiquiti (UniFi), Aruba, Fortinet, and other enterprise-grade vendors. We recommend the best fit based on your needs and budget." },
        { q: "How long does a typical installation take?", a: "Small office installations typically take 1-3 days. Larger projects with structured cabling and multiple floors may take 1-2 weeks. We provide a detailed timeline in your proposal." },
        { q: "Do you offer network maintenance after installation?", a: "Yes. We can provide ongoing managed IT support that includes network monitoring, firmware updates, and troubleshooting as part of our managed IT service." },
      ]}
    />
  );
}

function SecurityCamerasPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Camera}
      title="UniFi Protect, Hikvision &amp; Axis Security Camera Installation in Tampa Bay"
      serviceName="security camera installation"
      subtitle="Certified IP camera installers for Tampa Bay businesses"
      intro="Protect your property, employees, and assets with professional-grade IP security camera systems from Ubiquiti UniFi Protect, Hikvision, Axis Communications, Dahua, Amcrest, and Reolink. We handle camera layout design, installation, NVR or cloud recording, remote viewing, and integration with access control."
      brands={[
        { name: "UniFi Protect", logoUrl: `${process.env.PUBLIC_URL}/brands/unifi.png` },
        { name: "Amcrest", logoUrl: `${process.env.PUBLIC_URL}/brands/amcrest.png` },
        { name: "Hikvision", logoUrl: `${process.env.PUBLIC_URL}/brands/hikvision.png` },
        { name: "Reolink", logoUrl: `${process.env.PUBLIC_URL}/brands/reolink.png` },
        { name: "Axis", logoUrl: `${process.env.PUBLIC_URL}/brands/axis.png` },
        { name: "Dahua", logoUrl: `${process.env.PUBLIC_URL}/brands/dahua.png` },
      ]}
      features={[
        { title: "Camera System Design", text: "We survey your property, identify coverage needs, and design a camera layout that eliminates blind spots and covers all critical areas." },
        { title: "HD & 4K IP Cameras", text: "Commercial-grade IP cameras with high-resolution video, night vision, wide-angle lenses, and weather-resistant housings for indoor and outdoor use." },
        { title: "Remote Viewing", text: "View live and recorded footage from any device — phone, tablet, or computer — from anywhere with an internet connection." },
        { title: "Cloud & Local Recording", text: "Flexible storage options including on-site NVR recording, cloud storage, or hybrid solutions based on your retention requirements." },
        { title: "Motion Detection & Alerts", text: "Intelligent motion-triggered recording with real-time push notifications so you know immediately when something needs attention." },
        { title: "Access Control Integration", text: "Pair cameras with door access systems, intercoms, and gate controls for a unified physical security solution." },
      ]}
      benefits={[
        "Deter theft, vandalism, and unauthorized access to your business",
        "Crystal clear HD and 4K footage for identification and evidence",
        "View your cameras from anywhere on your phone or computer",
        "Professional installation with clean cable runs and concealed wiring",
        "Scalable systems that expand easily as your needs grow",
        "Local Tampa Bay team for installation, training, and ongoing support",
      ]}
      relatedServices={[
        { label: "Network Installation", page: "network-installation" },
        { label: "Managed IT Support", page: "managed-it-services" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "IT Consulting", page: "it-consulting" },
      ]}
      faq={[
        { q: "How many cameras do I need?", a: "It depends on your property size and coverage requirements. We provide a free site survey to determine the optimal number and placement of cameras for your business." },
        { q: "Can I view cameras on my phone?", a: "Yes. All of our camera systems include mobile app access so you can view live and recorded footage from your smartphone or tablet at any time." },
        { q: "How long is footage stored?", a: "Storage duration depends on your setup. Local NVR systems typically store 30-90 days of footage. Cloud storage plans offer flexible retention periods. We help you choose the right option." },
        { q: "Do you service existing camera systems?", a: "Yes. We can assess, repair, upgrade, or expand existing camera systems, regardless of the original installer. We also offer maintenance plans." },
      ]}
    />
  );
}

function ServerCloudPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Server}
      title="Synology NAS, Server &amp; Cloud Infrastructure in Tampa Bay"
      serviceName="server and cloud infrastructure"
      subtitle="Synology, on-premise servers, and cloud migration"
      intro="From Synology NAS installation and on-premise servers to cloud migration and hybrid environments, we build infrastructure that keeps your data available, your applications running, and your business protected against downtime and data loss — including local backup, virtualization, and disaster recovery."
      features={[
        { title: "Server Installation", text: "Physical server deployment including rack mounting, OS installation, Active Directory configuration, and integration with your existing network." },
        { title: "Cloud Migration", text: "Plan and execute migrations to AWS, Azure, Google Cloud, or Microsoft 365 with minimal downtime and full data verification." },
        { title: "Hybrid Environments", text: "Design infrastructure that balances on-premise and cloud resources for the best combination of performance, cost, and data control." },
        { title: "Backup & Disaster Recovery", text: "Automated backup solutions with offsite replication, tested recovery procedures, and documented RPO/RTO targets for your business." },
        { title: "Virtualization", text: "VMware and Hyper-V virtualization to consolidate hardware, improve resource utilization, and simplify server management." },
        { title: "Storage Solutions", text: "Network-attached storage (NAS), SAN configuration, and shared file systems designed for performance, redundancy, and easy collaboration." },
      ]}
      benefits={[
        "Infrastructure designed for your specific workloads and growth plans",
        "Tested backup and recovery procedures that protect your business data",
        "Cloud solutions that reduce hardware costs while improving reliability",
        "Virtualization that maximizes your existing hardware investment",
        "Ongoing monitoring and management through our managed IT service",
        "Local Tampa Bay engineers available for on-site server work",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it-services" },
        { label: "Network Installation", page: "network-installation" },
        { label: "Business Web Hosting", page: "hosting" },
        { label: "IT Consulting", page: "it-consulting" },
      ]}
      faq={[
        { q: "Should I use on-premise servers or the cloud?", a: "It depends on your workloads, compliance requirements, and budget. Many businesses benefit from a hybrid approach. We assess your situation and recommend the best fit." },
        { q: "Can you migrate our data to the cloud?", a: "Yes. We plan and execute cloud migrations to platforms like Microsoft 365, Azure, AWS, and Google Cloud with full data verification and minimal disruption." },
        { q: "How do you handle backups?", a: "We implement automated backup solutions with local and offsite copies, scheduled verification, and documented recovery procedures tailored to your business needs." },
        { q: "Do you provide ongoing server management?", a: "Yes. Server monitoring, patching, and management are included in our managed IT service. We proactively maintain your infrastructure to prevent issues." },
      ]}
    />
  );
}

function WebsiteDevelopmentPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Monitor}
      title="Website Development"
      serviceName="website development"
      subtitle="Professional business websites for Tampa Bay companies"
      intro="Your website is often the first impression customers have of your business. We build modern, mobile-friendly websites designed to look professional, load fast, rank in search engines, and convert visitors into leads and customers."
      features={[
        { title: "Responsive Design", text: "Websites that look and work perfectly on every device — desktop, tablet, and mobile — with modern layouts and clean typography." },
        { title: "SEO Foundations", text: "Semantic HTML, meta tags, structured data, page speed optimization, and content structure that helps your site rank in Google and other search engines." },
        { title: "Lead Generation", text: "Contact forms, quote request CTAs, click-to-call buttons, and conversion tracking designed to turn website visitors into business inquiries." },
        { title: "CMS Management", text: "Content management systems that let you update text, images, and pages without needing a developer for everyday changes." },
        { title: "Performance Optimization", text: "Fast load times through image optimization, code splitting, caching strategies, and CDN delivery that improve both user experience and SEO." },
        { title: "Ongoing Support", text: "Post-launch support for updates, content changes, security patches, and feature additions — so your site stays current and secure." },
      ]}
      benefits={[
        "Professional design that builds trust with visitors and customers",
        "Mobile-first approach ensures great experience on all devices",
        "SEO foundations that help you rank for local and industry searches",
        "Lead generation features that convert traffic into business inquiries",
        "Pair with managed hosting for a complete website solution",
        "Local Tampa Bay team for ongoing support and collaboration",
      ]}
      relatedServices={[
        { label: "Business Web Hosting", page: "hosting" },
        { label: "Managed IT Support", page: "managed-it-services" },
        { label: "IT Consulting & Strategy", page: "it-consulting" },
        { label: "Network Installation", page: "network-installation" },
      ]}
      faq={[
        { q: "How long does it take to build a website?", a: "A typical business website takes 4-8 weeks from kickoff to launch. Timeline depends on scope, content readiness, and revision rounds. We provide a detailed schedule in your proposal." },
        { q: "Can you redesign my existing website?", a: "Yes. We handle full redesigns, migrations from other platforms, and incremental improvements to existing sites. We assess your current site and recommend the best approach." },
        { q: "Do you provide hosting for the websites you build?", a: "Yes. We offer managed hosting plans with SSL, daily backups, monitoring, and support — designed specifically for business websites. See our hosting page for plan details." },
        { q: "Will my website be optimized for search engines?", a: "Yes. Every website we build includes SEO foundations: semantic HTML, meta tags, structured data, fast load times, and mobile-friendly design. We also offer ongoing SEO services." },
      ]}
    />
  );
}

function ItConsultingPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={LineChart}
      title="IT Consulting & Strategy"
      serviceName="IT consulting"
      subtitle="Technology guidance for Tampa Bay businesses"
      intro="Make smarter technology decisions with strategic IT consulting. We provide assessments, infrastructure planning, compliance guidance, vendor evaluation, and IT roadmaps that align your technology investments with your business goals."
      features={[
        { title: "Technology Assessments", text: "Comprehensive evaluation of your current infrastructure, security posture, and technology stack with prioritized recommendations for improvement." },
        { title: "Infrastructure Planning", text: "Design technology architectures for new offices, expansions, relocations, or major upgrades — with clear specifications and budget estimates." },
        { title: "Compliance Guidance", text: "Navigate HIPAA, PCI-DSS, SOC 2, and industry-specific compliance requirements with documentation and controls tailored to your business." },
        { title: "Vendor Evaluation", text: "Objective assessment and comparison of technology vendors, platforms, and solutions so you make informed purchasing decisions." },
        { title: "Budget Planning", text: "IT budget development including capital vs. operational expense analysis, lifecycle planning, and ROI projections for proposed investments." },
        { title: "IT Roadmaps", text: "Multi-year technology plans that align IT investments with business growth, risk reduction, and operational improvement goals." },
      ]}
      benefits={[
        "Objective technology advice not tied to specific vendor relationships",
        "Clear, actionable recommendations with priority and budget context",
        "Compliance guidance that prepares your business for audits and certifications",
        "Strategic planning that prevents expensive reactive technology purchases",
        "Experience across industries including healthcare, professional services, and retail",
        "Ongoing advisory available as part of our managed IT service",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it-services" },
        { label: "Network Installation", page: "network-installation" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Website Development", page: "website-development" },
      ]}
      faq={[
        { q: "When should I hire an IT consultant?", a: "Common triggers include office moves, rapid growth, compliance requirements, aging infrastructure, security concerns, or the need for a technology roadmap. If you're unsure, a free initial consultation can help." },
        { q: "Do you only consult, or do you implement too?", a: "Both. We provide strategic consulting as a standalone service, and we can also implement the solutions we recommend through our other service lines." },
        { q: "How is consulting priced?", a: "Consulting can be project-based (fixed scope and fee), hourly, or included as part of a managed IT agreement. We provide clear pricing in every proposal." },
        { q: "Can you help with compliance requirements?", a: "Yes. We help businesses understand and meet HIPAA, PCI-DSS, SOC 2, and other regulatory and industry compliance requirements through assessments, documentation, and controls implementation." },
      ]}
    />
  );
}

// =========================================================================
// SOLUTIONS PAGE
// =========================================================================

function SolutionsPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  const solutions = [
    {
      icon: Building2,
      title: "Setting up a new office",
      text: "Moving into a new space? We handle network cabling, Wi-Fi setup, phone systems, printers, security cameras, and workstation deployment so your team is productive from day one.",
      services: ["Network Installation", "Security Cameras", "Managed IT"],
      cta: "network-installation" as PageKey,
    },
    {
      icon: Wifi,
      title: "Fixing slow or unreliable Wi-Fi",
      text: "Dead zones, dropped connections, and slow speeds cost your team hours every week. We audit your wireless environment and deploy enterprise-grade access points for reliable coverage.",
      services: ["Network Installation", "Managed IT"],
      cta: "network-installation" as PageKey,
    },
    {
      icon: Camera,
      title: "Adding security cameras",
      text: "Protect your business with professional-grade surveillance. We design camera layouts, install equipment, set up remote viewing, and configure cloud or local recording.",
      services: ["Security Cameras", "Network Installation"],
      cta: "security-cameras" as PageKey,
    },
    {
      icon: Monitor,
      title: "Building a professional website",
      text: "Your website is often the first impression customers have of your business. We build modern, mobile-friendly sites designed to convert visitors into leads and customers.",
      services: ["Website Development", "Web Hosting", "SEO"],
      cta: "website-development" as PageKey,
    },
    {
      icon: Headphones,
      title: "Getting ongoing IT support",
      text: "Stop putting out fires. Our managed IT service provides proactive monitoring, helpdesk support, security updates, and technology planning so issues are handled before they impact your work.",
      services: ["Managed IT", "Server & Cloud"],
      cta: "managed-it-services" as PageKey,
    },
    {
      icon: Cloud,
      title: "Choosing the right web hosting",
      text: "Not all hosting is equal. We offer managed hosting plans with SSL, daily backups, monitoring, and real support — designed for business websites that need to stay online.",
      services: ["Web Hosting", "Website Development"],
      cta: "hosting" as PageKey,
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("Solutions")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Technology solutions for common business challenges")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "Start with the problem you are trying to solve. We will match you with the right service, explain what is involved, and give you a clear path to getting it done."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Solution cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {solutions.map((sol) => (
            <article
              key={sol.title}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                <sol.icon className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {t(sol.title)}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {t(sol.text)}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {sol.services.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600"
                  >
                    {t(s)}
                  </span>
                ))}
              </div>
              <PageLink
                to={sol.cta}
                setPage={setPage}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 transition hover:text-sky-800"
              >
                {t("See how we help")} <ChevronRight className="h-4 w-4" />
              </PageLink>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-sky-50">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">
                {t("Need help choosing the right solution?")}
              </h2>
              <p className="mt-2 text-slate-600">
                {t("Our team will assess your situation and recommend the best path forward — no obligation.")}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <CtaButton
                to="contact"
                setPage={setPage}
                onClick={() => trackCta("quote_click", "solutions_cta", "solutions")}
                ctaName="solutions-quote"
                page="solutions"
              >
                {t("Get a Free Quote")}
              </CtaButton>
              <CtaButton variant="secondary" to="hosting" setPage={setPage}>
                {t("View Hosting Plans")}
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// AREAS SERVED PAGE
// =========================================================================

function AreasPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  const areas = [
    {
      name: "Tampa",
      desc: "Managed IT, network installation, security cameras, web hosting, and website development for businesses throughout Tampa and Hillsborough County.",
    },
    {
      name: "Clearwater",
      desc: "Our home base. Full-service IT support, networking, surveillance, and web solutions for Clearwater businesses and surrounding areas.",
    },
    {
      name: "Largo",
      desc: "On-site and remote IT services, hosting, and technology support for businesses in Largo and mid-Pinellas County.",
    },
    {
      name: "St. Petersburg",
      desc: "Comprehensive business technology services including managed IT, web development, camera systems, and hosting for St. Pete companies.",
    },
    {
      name: "Pinellas Park",
      desc: "Network installation, IT support, security cameras, and business hosting services for Pinellas Park offices and commercial spaces.",
    },
    {
      name: "Brandon",
      desc: "Managed IT, networking, cloud infrastructure, and web services for businesses in the Brandon and eastern Hillsborough area.",
    },
  ];

  const serviceCategories = [
    "Managed IT support",
    "Network installation & Wi-Fi",
    "Security camera systems",
    "Business web hosting",
    "Website development",
    "Server & cloud infrastructure",
    "IT consulting & strategy",
    "Ongoing technology support",
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("Service coverage")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("IT services across the Tampa Bay area")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "SightLine Networks provides managed IT, web hosting, networking, security cameras, and website development to businesses throughout Tampa Bay. We offer both on-site and remote support."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Area cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((area) => (
            <article
              key={area.name}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-sky-600" />
                <h2 className="text-lg font-bold text-slate-900">
                  {area.name}
                </h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                {t(area.desc)}
              </p>
              <PageLink
                to="contact"
                setPage={setPage}
                onClick={() => trackCta("area_quote", area.name, "areas")}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 transition hover:text-sky-800"
              >
                {t("Get support in")} {area.name}{" "}
                <ChevronRight className="h-4 w-4" />
              </PageLink>
            </article>
          ))}
        </div>
      </section>

      {/* Service categories */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            {t("Services available across Tampa Bay")}
          </h2>
          <p className="mt-3 max-w-2xl text-slate-600">
            {t("Every service we offer is available to businesses throughout our coverage area, with on-site support where needed.")}
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {serviceCategories.map((svc) => (
              <div
                key={svc}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
              >
                <CheckCircle2 className="h-4 w-4 shrink-0 text-sky-500" />
                {t(svc)}
              </div>
            ))}
          </div>
          <CtaButton
            to="contact"
            setPage={setPage}
            onClick={() => trackCta("quote_click", "areas_bottom", "areas")}
            className="mt-8"
            ctaName="areas-quote"
            page="areas"
          >
            {t("Request Local Support")}
          </CtaButton>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// ABOUT PAGE
// =========================================================================

function AboutPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("About us")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Your Tampa Bay technology partner")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "SightLine Networks was founded to give Tampa Bay businesses access to the same caliber of IT services, hosting, and technology infrastructure that large enterprises rely on — without the complexity or inflated costs."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Story */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("Built for business. Based in Tampa Bay.")}
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              <p>
                {t(
                  "We started SightLine Networks because we saw too many local businesses struggling with unreliable IT vendors, confusing pricing, and technology that was holding them back instead of moving them forward."
                )}
              </p>
              <p>
                {t(
                  "Today, we serve companies across Tampa, Clearwater, Largo, St. Petersburg, and beyond with managed IT, network installation, security cameras, web hosting, website development, and ongoing technology support."
                )}
              </p>
              <p>
                {t(
                  "Our approach is simple: understand what your business actually needs, deliver it professionally, and be available when you need us. No jargon, no hidden fees, no excuses."
                )}
              </p>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {[
              { value: "500+", label: "Projects completed", icon: CheckCircle2 },
              { value: "24/7", label: "System monitoring", icon: Clock3 },
              { value: "99.9%", label: "Hosting uptime", icon: Server },
              { value: "Same-day", label: "Urgent response", icon: Zap },
            ].map(({ value, label, icon: Icon }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center shadow-sm"
              >
                <Icon className="mb-3 h-6 w-6 text-sky-600" />
                <div className="text-2xl font-bold text-slate-900">{t(value)}</div>
                <div className="mt-1 text-sm text-slate-500">{t(label)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("What guides how we work")}
            </h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: "Clarity over complexity", text: "We explain technology in plain language, give clear proposals, and make sure you understand what you are paying for and why." },
              { title: "Proactive, not reactive", text: "We monitor systems, apply updates, and identify risks before they become problems. Prevention is always cheaper than recovery." },
              { title: "Local presence, real support", text: "We are based in the Tampa Bay area. When you need on-site help, we show up. When you call, a real person answers." },
              { title: "Right-sized solutions", text: "We recommend what your business actually needs — not the most expensive option. Solutions should match your size and goals." },
              { title: "Long-term relationships", text: "We build technology partnerships, not transactional vendor relationships. Our best clients have been with us for years." },
              { title: "Transparent pricing", text: "No hidden fees, no surprise invoices, no bait-and-switch. You will know exactly what services cost before we start." },
            ].map(({ title, text }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="text-base font-semibold text-slate-900">{t(title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="rounded-2xl bg-sky-600 px-8 py-12 text-center shadow-lg shadow-sky-200/30">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            {t("Let's talk about what your business needs")}
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-50">
            {t("Whether you need managed IT, a new website, hosting, or a full technology overhaul — we're happy to discuss it.")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              to="contact"
              setPage={setPage}
              onClick={() => trackCta("quote_click", "about_cta", "about")}
              ctaName="about-quote"
              page="about"
            >
              {t("Get a Free Quote")}
            </CtaButton>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
            >
              <Phone className="h-4 w-4" /> {t("Call")} {PHONE}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// CONTACT PAGE
// =========================================================================

function ContactPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error" | "spam"
  >("idle");
  // Bot filtering (shared helpers — see isSpamSubmission() near submitContactLead).
  const mountedAt = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleHoneypotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHoneypot(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Duplicate-submit guard: ignore extra clicks while a request is in flight.
    if (status === "sending") return;

    if (isSpamSubmission(honeypot, mountedAt.current)) {
      // Fake success so bots gain nothing from probing the form. Uses the
      // "spam" status — NOT "success" — so the Google Ads conversion element
      // (p.text-emerald-600 in the success branch below) never becomes
      // visible to a bot.
      setStatus("spam");
      return;
    }

    setStatus("sending");
    trackCta("form_submit", "contact_form", "contact");

    try {
      await submitContactLead({
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        subject: form.service || "General Inquiry",
        message: form.message || "No additional message provided",
        source: "main-website",
      });

      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
      // Success-only lead conversion (GA4 + dataLayer).
      trackLeadConversion("main-website", "contact");
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus("error");
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("Get in touch")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Let's discuss your project")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "Tell us what your business needs and we will get back to you with a clear recommendation and a free, no-obligation quote — usually within one business day."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Form + sidebar */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Form */}
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900">
              {t("Request a free quote")}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {t("Fill out the form and our team will respond within one business day.")}
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
              id="contact-form"
              data-form="contact"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t("Full Name *")}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder={t("Your name")}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t("Email *")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder={t("you@company.com")}
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t("Phone")}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder={t("(555) 000-0000")}
                  />
                </div>
                <div>
                  <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t("Company")}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder={t("Your company")}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t("What do you need help with? *")}
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={form.service}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="">{t("Select a service")}</option>
                  <option value="managed-it">{t("Managed IT Support")}</option>
                  <option value="business-wifi">{t("Business Wi-Fi")}</option>
                  <option value="network">{t("Network Installation")}</option>
                  <option value="unifi">{t("UniFi")}</option>
                  <option value="cameras">{t("Security Cameras")}</option>
                  <option value="structured-cabling">{t("Structured Cabling")}</option>
                  <option value="server">{t("Server & Cloud")}</option>
                  <option value="website">{t("Website Development")}</option>
                  <option value="hosting">{t("Web Hosting")}</option>
                  <option value="consulting">{t("IT Consulting")}</option>
                  <option value="other">{t("Other / Not Sure")}</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t("Tell us about your project")}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                  placeholder={t("Describe your needs, timeline, or any questions you have...")}
                />
              </div>

              {/* Honeypot — invisible to humans, irresistible to bots. */}
              <div aria-hidden="true" className="hidden">
                <label htmlFor="contact-website-url">Leave this field empty</label>
                <input
                  type="text"
                  id="contact-website-url"
                  name={HONEYPOT_FIELD}
                  value={honeypot}
                  onChange={handleHoneypotChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <CtaButton
                type="submit"
                ctaName="contact-submit"
                page="contact"
                disabled={status === "sending"}
                className="w-full sm:w-auto"
              >
                {status === "sending"
                  ? t("Sending...")
                  : status === "success"
                  ? t("Quote Requested!")
                  : t("Submit Quote Request")}
              </CtaButton>

              {status === "success" && (
                <p className="text-sm font-medium text-emerald-600">
                  {t("Thank you! We received your request and will be in touch within one business day.")}
                </p>
              )}
              {status === "error" && (
                <p className="text-sm font-medium text-red-600">
                  {t("Something went wrong. Please call us at")} {PHONE}{" "}
                  {t("or email")} {EMAIL}.
                </p>
              )}
              {/* Bot-only fake success: no emerald class, so GTM's Ads
                  conversion trigger cannot see it. */}
              {status === "spam" && (
                <p className="text-sm font-medium text-slate-600" aria-hidden="true">
                  {t("Thank you! We received your request and will be in touch within one business day.")}
                </p>
              )}
            </form>
          </div>

          {/* Sidebar */}
          <div className="flex flex-col gap-6">
            {/* Direct contact */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">
                {t("Prefer to talk directly?")}
              </h3>
              <div className="mt-4 space-y-4">
                <a
                  href={PHONE_HREF}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  onClick={() => trackCta("phone_click", "contact_sidebar", "contact")}
                >
                  <Phone className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">{PHONE}</div>
                    <div className="text-xs text-slate-500">{t("Call or text")}</div>
                  </div>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  onClick={() => trackCta("email_click", "contact_sidebar", "contact")}
                >
                  <Mail className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">{EMAIL}</div>
                    <div className="text-xs text-slate-500">{t("Email us anytime")}</div>
                  </div>
                </a>
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                  <MapPin className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">Clearwater, FL</div>
                    <div className="text-xs text-slate-500">
                      {t("Serving the greater Tampa Bay area")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust signals */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="text-lg font-semibold text-slate-900">
                {t("Why businesses choose SightLine")}
              </h3>
              <ul className="mt-4 space-y-3">
                {[
                  "Free, no-obligation quotes",
                  "Response within one business day",
                  "Local Tampa Bay team",
                  "Transparent pricing — no hidden fees",
                  "500+ projects completed",
                  "Certified engineers (Cisco, Microsoft, VMware)",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-sm text-slate-600"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-sky-500" />
                    {t(item)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Service links */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">
                {t("Explore our services")}
              </h3>
              <div className="mt-4 flex flex-col gap-2">
                {[
                  { label: "View all services", page: "services" as PageKey },
                  { label: "Hosting plans & pricing", page: "hosting" as PageKey },
                  { label: "Solutions by need", page: "solutions" as PageKey },
                  { label: "Areas we serve", page: "areas" as PageKey },
                ].map(({ label, page: p }) => (
                  <PageLink
                    key={label}
                    to={p}
                    setPage={setPage}
                    className="flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                  >
                    {t(label)}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </PageLink>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// PRIVACY NOTICE BANNER (CCPA/CPRA notice-only)
// =========================================================================

function PrivacyNoticeBanner({ setPage }: { setPage: (page: PageKey) => void }) {
  const { lang, t } = useLanguage();
  // Prerender/SSR safe: start hidden so the banner is never baked into the
  // static HTML produced by react-snap. We only reveal it after reading
  // localStorage on the client.
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    // Don't surface the banner during the react-snap prerender snapshot, so it
    // is never baked into the static HTML (which would flash for returning,
    // already-consented visitors until hydration hides it again).
    if (typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent)) {
      return;
    }
    // Consent is read from a cookie shared across all SightLine subdomains, so
    // an acknowledgment made on the hosting platform counts here too — global
    // consent, not per-application.
    if (!hasAcknowledgedConsent()) setVisible(true);
  }, []);

  // Slide/fade the card in on the frame after it mounts (the motion-reduce:
  // classes below make it appear instantly for reduced-motion users).
  useEffect(() => {
    if (!visible) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [visible]);

  const dismiss = () => {
    writeAcknowledgedConsent();
    setVisible(false);
  };

  const goToPrivacy = (anchor?: string) => {
    setPage("privacy");
    if (anchor && typeof window !== "undefined") {
      // Wait for the privacy page to mount (and the page-change scroll-to-top
      // effect to run) before scrolling to the requested section.
      window.setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  };

  if (!visible) return null;

  const linkCls =
    "rounded text-sky-600 underline-offset-2 transition hover:text-sky-700 hover:underline focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2";

  return (
    <div
      role="region"
      aria-label={t("Privacy notice")}
      className={`fixed z-50 inset-x-4 bottom-4 sm:inset-x-auto sm:left-6 sm:bottom-6 sm:w-[26rem] transition-all duration-500 ease-out motion-reduce:transition-none ${
        entered
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100"
      }`}
    >
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10">
        {/* Brand accent strip */}
        <div className="h-1 w-full bg-gradient-to-r from-sky-500 to-cyan-400" />

        <div className="p-5 sm:p-6">
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("Dismiss privacy notice")}
            className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-3 pr-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600 ring-1 ring-sky-100">
              <Shield className="h-5 w-5" />
            </span>
            <h2 className="font-['Space_Grotesk'] text-base font-bold tracking-tight text-slate-900">
              {t("Your privacy, your choice")}
            </h2>
          </div>

          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            {t(
              "We use cookies for analytics (Google Analytics) and advertising (Google Ads), which may share identifiers like your IP and cookie IDs with those providers. By using this site you consent — California residents can opt out anytime."
            )}
          </p>

          <div className="mt-3 flex flex-col items-start gap-1.5 text-[13px] font-medium">
            <a
              href={urlFor("privacy", lang)}
              onClick={(e) => {
                e.preventDefault();
                goToPrivacy();
              }}
              className={linkCls}
            >
              {t("Privacy Policy")}
            </a>
            <a
              href={`${urlFor("privacy", lang)}#do-not-sell`}
              onClick={(e) => {
                e.preventDefault();
                goToPrivacy("do-not-sell");
              }}
              className={linkCls}
            >
              {t("Do Not Sell or Share My Personal Information")}
            </a>
          </div>

          <div className="mt-5">
            <CtaButton
              variant="primary"
              ctaName="privacy-consent-ack"
              onClick={dismiss}
              className="w-full"
            >
              {t("Got it")}
            </CtaButton>
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// PRIVACY POLICY PAGE
// =========================================================================

function PrivacyPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const { lang, t } = useLanguage();
  const h2 = "mt-10 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl";
  const p = "mt-4 text-sm leading-relaxed text-slate-600";
  const ul = "mt-4 space-y-2 text-sm leading-relaxed text-slate-600";
  const li = "flex gap-2";
  const linkCls =
    "rounded text-sky-600 underline-offset-2 transition hover:text-sky-700 hover:underline focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2";

  const Bullet = ({ children }: { children: React.ReactNode }) => (
    <li className={li}>
      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
      <span>{children}</span>
    </li>
  );

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:py-20">
          <SectionLabel>{t("Legal")}</SectionLabel>
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            {t("Privacy Policy")}
          </h1>
          <p className="mt-4 text-sm font-medium text-slate-500">
            {t("Last Updated: June 27, 2026")}
          </p>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            {t(
              'SightLine Networks LLC ("we," "us," or "our") respects your privacy. This Privacy Policy explains what personal information we collect, how we use and share it, and the rights you have — including rights specific to California residents under the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA).'
            )}
          </p>

          {/* The English text remains the controlling version of this policy —
              say so explicitly on the translated page rather than implying the
              Spanish text is independently binding. */}
          {lang !== "en" && (
            <p className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-600">
              {t(
                "This Spanish translation is provided for convenience. In case of any discrepancy, the English version of this Privacy Policy governs."
              )}{" "}
              <a href={urlFor("privacy", "en")} hrefLang="en" className={linkCls}>
                {t("View this page in English")}
              </a>
            </p>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-4xl px-6 py-16 lg:py-20">
        {/* 1 */}
        <h2 className={h2}>{t("1. Information We Collect")}</h2>
        <p className={p}>{t("We collect the following categories of personal information:")}</p>
        <ul className={ul}>
          <Bullet>
            {t("Identifiers: name, email address, phone number, IP address, and online identifiers (such as cookie IDs) when you contact us, request a quote, create an account, or browse the site.")}
          </Bullet>
          <Bullet>
            {t("Commercial information: services or plans you purchase or inquire about, and billing details (payment card data is processed by our payment provider and is not stored on our servers).")}
          </Bullet>
          <Bullet>
            {t("Internet or other electronic network activity: pages viewed, links clicked, referring URLs, and how you interact with our site, collected through cookies and analytics tools.")}
          </Bullet>
          <Bullet>
            {t("Geolocation data: approximate location derived from your IP address.")}
          </Bullet>
          <Bullet>
            {t("Inferences drawn from the above to understand your interest in our services.")}
          </Bullet>
        </ul>
        <p className={p}>
          <span className="font-semibold text-slate-900">
            {t("Sources of this information.")}
          </span>{" "}
          {t("We collect personal information directly from you (for example, when you contact us, request a quote, or create an account), automatically from your device and browser as you use our site (through cookies and similar technologies), and from service providers such as our analytics and advertising partners.")}
        </p>
        <p className={p}>
          {t("We do not knowingly collect sensitive personal information beyond what you voluntarily provide to us.")}
        </p>

        {/* 2 */}
        <h2 className={h2}>{t("2. How We Use Your Information")}</h2>
        <ul className={ul}>
          <Bullet>{t("To provide, operate, and maintain our website and services;")}</Bullet>
          <Bullet>{t("To respond to your inquiries, quotes, and support requests;")}</Bullet>
          <Bullet>{t("To process transactions and manage accounts;")}</Bullet>
          <Bullet>{t("To measure and improve site performance and marketing;")}</Bullet>
          <Bullet>{t("To deliver and measure advertising;")}</Bullet>
          <Bullet>
            {t("To comply with legal obligations and protect against fraud and abuse.")}
          </Bullet>
        </ul>

        {/* 3 */}
        <h2 className={h2}>{t("3. Cookies and Tracking Technologies")}</h2>
        <p className={p}>
          {t("We and our service providers use cookies, pixels, tags, and similar technologies to operate the site, remember preferences, analyze traffic, and support advertising. Specifically, we use:")}
        </p>
        <ul className={ul}>
          <Bullet>{t("Google Tag Manager — to manage our tags;")}</Bullet>
          <Bullet>{t("Google Analytics 4 (GA4) — to measure site traffic and usage;")}</Bullet>
          <Bullet>
            {t("Google Ads — to measure advertising performance and conversions.")}
          </Bullet>
        </ul>
        <p className={p}>
          {t("These tools may set cookies and receive online identifiers (such as your IP address and cookie IDs) directly from your browser. You can control cookies through your browser settings. You can opt out of certain Google advertising at")}{" "}
          <a
            href="https://myadcenter.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            https://myadcenter.google.com
          </a>{" "}
          {t("and out of Google Analytics via the Google Analytics Opt-out Browser Add-on at")}{" "}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            https://tools.google.com/dlpage/gaoptout
          </a>
          .
        </p>

        {/* 4 */}
        <h2 className={h2}>{t("4. How We Share Information")}</h2>
        <p className={p}>{t("We may disclose personal information to:")}</p>
        <ul className={ul}>
          <Bullet>
            {t("Service providers who perform services on our behalf (for example, hosting, payment processing, email delivery, analytics, and advertising);")}
          </Bullet>
          <Bullet>
            {t('Advertising and analytics partners (such as Google) for analytics and ad measurement — under California law, this disclosure for cross-context behavioral advertising may be considered "sharing";')}
          </Bullet>
          <Bullet>
            {t("Authorities or others for legal, safety, and compliance purposes, and in connection with a business transfer.")}
          </Bullet>
        </ul>
        <p className={p}>
          {t("We do not sell your personal information for monetary value.")}
        </p>
        <p className={p}>
          <span className="font-semibold text-slate-900">
            {t("Categories of third parties.")}
          </span>{" "}
          {t("We may disclose personal information to analytics and advertising providers (such as Google); payment processors; hosting, infrastructure, and email or communications providers; and professional advisors, regulators, or law enforcement where permitted or required by law.")}
        </p>
        <p className={p}>
          <span className="font-semibold text-slate-900">
            {t("Personal information shared for advertising.")}
          </span>{" "}
          {t('The categories of personal information that may be "shared" (as defined under California law) for cross-context behavioral advertising are identifiers (such as cookie IDs and IP address) and internet or other electronic network activity (such as pages viewed and interactions). We do not share sensitive personal information for this purpose, and we do not knowingly share the personal information of consumers under 16 years of age.')}
        </p>

        {/* 5 */}
        <h2 className={h2}>{t("5. Your California Privacy Rights")}</h2>
        <p className={p}>{t("If you are a California resident, you have the right to:")}</p>
        <ul className={ul}>
          <Bullet>
            {t("Know what personal information we collect, use, disclose, and share;")}
          </Bullet>
          <Bullet>{t("Access a copy of your personal information;")}</Bullet>
          <Bullet>{t("Request deletion of your personal information;")}</Bullet>
          <Bullet>{t("Correct inaccurate personal information;")}</Bullet>
          <Bullet>
            {t('Opt out of the "sale" or "sharing" of your personal information;')}
          </Bullet>
          <Bullet>{t("Limit the use of sensitive personal information;")}</Bullet>
          <Bullet>
            {t("Not be discriminated against for exercising your rights.")}
          </Bullet>
        </ul>

        {/* 6 */}
        <h2 id="do-not-sell" className={`${h2} scroll-mt-24`}>
          {t("6. Do Not Sell or Share My Personal Information")}
        </h2>
        <p className={p}>
          {t('We do not sell your personal information for monetary value. However, our use of analytics and advertising cookies (such as Google Analytics and Google Ads) may be considered "sharing" of personal information for cross-context behavioral advertising under California law. To opt out, you can:')}
        </p>
        <ul className={ul}>
          <Bullet>
            {t("Adjust your cookie and ad settings as described in Section 3;")}
          </Bullet>
          <Bullet>
            {t("Enable a Global Privacy Control (GPC) signal in your browser — we automatically detect and honor GPC as a request to opt out of the sale or sharing of your personal information; and/or")}
          </Bullet>
          <Bullet>
            {t("Contact us at")}{" "}
            <a href={`mailto:${EMAIL}`} className={linkCls}>
              {EMAIL}
            </a>{" "}
            {t('with the subject "Do Not Sell or Share."')}
          </Bullet>
        </ul>

        {/* 7 */}
        <h2 className={h2}>{t("7. Global Privacy Control (GPC)")}</h2>
        <p className={p}>
          {t("Some browsers and extensions let you broadcast a Global Privacy Control (GPC) signal. We automatically detect this signal and treat it as a request to opt out of the sale or sharing of your personal information for advertising and analytics — no account or sign-in is required. You can also limit these cookies through your browser settings and the Google opt-out tools described in Section 3, or email")}{" "}
          <a href={`mailto:${EMAIL}`} className={linkCls}>
            {EMAIL}
          </a>{" "}
          {t("to submit a request.")}
        </p>

        {/* 8 */}
        <h2 className={h2}>{t("8. How to Exercise Your Rights")}</h2>
        <p className={p}>
          {t("You, or an authorized agent acting on your behalf, may submit a request by emailing")}{" "}
          <a href={`mailto:${EMAIL}`} className={linkCls}>
            {EMAIL}
          </a>
          .{" "}
          {t("We will take steps to verify your request and will respond within the timeframes required by law. We will not discriminate against you for exercising your rights.")}
        </p>

        {/* 9 */}
        <h2 className={h2}>{t("9. Data Retention")}</h2>
        <p className={p}>
          {t("We retain each category of personal information for as long as necessary to fulfill the purposes described in this Policy. We determine the appropriate retention period based on the length of our relationship with you, the time needed to provide our services and support, our legal, tax, accounting, and regulatory obligations, and the need to resolve disputes and enforce our agreements. Analytics and advertising identifiers are retained according to the retention settings of the relevant provider (for example, Google Analytics). When personal information is no longer needed, we delete or de-identify it.")}
        </p>

        {/* 10 */}
        <h2 className={h2}>{t("10. Children's Privacy")}</h2>
        <p className={p}>
          {t("Our site is not directed to children under 16, and we do not knowingly collect personal information from them.")}
        </p>

        {/* 11 */}
        <h2 className={h2}>{t("11. Third-Party Links")}</h2>
        <p className={p}>
          {t("Our site may link to third-party websites. We are not responsible for their privacy practices.")}
        </p>

        {/* 12 */}
        <h2 className={h2}>{t("12. Changes to This Policy")}</h2>
        <p className={p}>
          {t('We may update this Policy from time to time. The "Last Updated" date above reflects the most recent revision.')}
        </p>

        {/* 13 */}
        <h2 className={h2}>{t("13. Contact Us")}</h2>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">SightLine Networks LLC</p>
          <p className="mt-1 text-sm text-slate-600">
            {t("Email:")}{" "}
            <a
              href={`mailto:${EMAIL}`}
              className={linkCls}
              onClick={() => trackCta("email_click", "privacy_contact", "privacy")}
            >
              {EMAIL}
            </a>
          </p>
          <div className="mt-5">
            <CtaButton
              variant="primary"
              ctaName="privacy-contact"
              page="privacy"
              to="contact"
              setPage={setPage}
            >
              {t("Contact Us")} <ArrowRight className="h-4 w-4" />
            </CtaButton>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// GOOGLE ADS LANDING PAGES
// =========================================================================
//
// Dedicated, conversion-focused pages built for paid search traffic —
// distinct from the informational service pages above. Each one drives one
// primary action (book a consultation) instead of inviting exploration of
// the whole site, and its headline is written to message-match the ad copy
// that sent the visitor here (see SEO_CONFIG["managed-it-services"] for the
// target search terms).
//
// The components below are intentionally generic so the next landing page
// is mostly new copy passed into these same shapes. The four Google Ads
// landing pages (/business-wifi, /unifi-installer, /camera-installation,
// /structured-cabling) are already built on these components — see the
// src/landing/* modules.

/** Full-bleed, centered hero for a single-goal landing page. */
function ServiceHero({
  eyebrow,
  title,
  subtitle,
  primaryLabel,
  onPrimary,
  ctaName,
  page,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  primaryLabel: string;
  onPrimary: () => void;
  ctaName: string;
  page: PageKey;
}) {
  const t = useT();
  return (
    <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-b from-sky-50/60 via-white to-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(14,165,233,0.08),transparent_50%)]" />
      <div className="relative mx-auto max-w-4xl px-6 py-16 text-center lg:py-24">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-sky-700">
          <MapPin className="h-3.5 w-3.5" />
          {t(eyebrow)}
        </div>
        <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
          {t(title)}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
          {t(subtitle)}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <CtaButton onClick={onPrimary} ctaName={ctaName} page={page}>
            {t(primaryLabel)} <ArrowRight className="h-4 w-4" />
          </CtaButton>
          {/* Secondary conversion path: a plain tel: link is click-to-call on
              every mobile browser with no extra markup required. */}
          <a
            href={PHONE_HREF}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            onClick={() => trackCta("phone_click", `${ctaName}_phone`, page)}
          >
            <Phone className="h-4 w-4" /> {t("Call Us")}: {PHONE}
          </a>
        </div>
      </div>
    </section>
  );
}

/** Row of short trust/value signals directly under the hero. */
function TrustBar({ items }: { items: Array<{ icon: typeof MapPin; label: string }> }) {
  const t = useT();
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-x-4 gap-y-4 px-6 py-6 sm:grid-cols-4">
        {items.map(({ icon: Icon, label }) => (
          <div
            key={label}
            className="flex items-center justify-center gap-2.5 text-center text-sm font-medium text-slate-700 sm:justify-start sm:text-left"
          >
            <Icon className="h-4 w-4 shrink-0 text-sky-600" />
            <span>{t(label)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Clean grid of industries/segments served — no icon variety, kept simple on purpose. */
function IndustryGrid({ items }: { items: string[] }) {
  const t = useT();
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((label) => (
        <div
          key={label}
          className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm font-medium text-slate-700 shadow-sm"
        >
          <Building2 className="h-4 w-4 shrink-0 text-sky-600" />
          {t(label)}
        </div>
      ))}
    </div>
  );
}

/** Four-up differentiator grid — same card treatment as the Home "Why choose us" section. */
function WhyChooseUs({
  items,
}: {
  items: Array<{ icon: typeof MapPin; title: string; text: string }>;
}) {
  const t = useT();
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {items.map(({ icon: Icon, title, text }) => (
        <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
            <Icon className="h-5 w-5" />
          </div>
          <h3 className="text-base font-semibold text-slate-900">{t(title)}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(text)}</p>
        </div>
      ))}
    </div>
  );
}

/** Pill row of the cities/areas a landing page covers. */
function ServiceArea({ cities }: { cities: string[] }) {
  return (
    <div className="flex flex-wrap justify-center gap-3">
      {cities.map((city) => (
        <span
          key={city}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm"
        >
          <MapPin className="h-3.5 w-3.5 text-sky-600" />
          {city}
        </span>
      ))}
    </div>
  );
}

/** Full-width closing CTA banner — same treatment as the Home bottom CTA. */
function LeadCta({
  title,
  text,
  primaryLabel,
  onPrimary,
  ctaName,
  page,
}: {
  title: string;
  text: string;
  primaryLabel: string;
  onPrimary: () => void;
  ctaName: string;
  page: PageKey;
}) {
  const t = useT();
  return (
    <section className="border-t border-slate-200 bg-sky-600">
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">{t(title)}</h2>
        <p className="mx-auto mt-4 max-w-xl text-sky-50">{t(text)}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <CtaButton variant="white" onClick={onPrimary} ctaName={ctaName} page={page}>
            {t(primaryLabel)} <ArrowRight className="h-4 w-4" />
          </CtaButton>
          <a
            href={PHONE_HREF}
            className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
            onClick={() => trackCta("phone_click", `${ctaName}_phone`, page)}
          >
            <Phone className="h-4 w-4" /> {t("Call")} {PHONE}
          </a>
        </div>
      </div>
    </section>
  );
}

/**
 * Short lead-capture form for a landing page.
 *
 * Shares its submission path with ContactPage via submitContactLead() —
 * see that function's comment for why. The success message keeps the exact
 * `text-emerald-600` class GTM's conversion trigger already watches for, plus
 * a stable `id`/`data-conversion-event` pair for any future, more specific
 * trigger. It renders ONLY after a real successful submission (never on
 * click, never while `status === "sending"`), so the Google Ads conversion
 * cannot fire from a bare button click.
 */
function LeadForm({
  page,
  source,
  formId,
  subject,
  submitLabel,
}: {
  page: PageKey;
  source: string;
  formId: string;
  subject: string;
  submitLabel: string;
}) {
  const t = useT();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error" | "spam"
  >("idle");
  // Bot filtering: mountedAt feeds the submit-speed time trap; honeypot is an
  // invisible field only bots fill. Both are shared helpers — see isSpamSubmission().
  const mountedAt = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleHoneypotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHoneypot(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Duplicate-submit guard: ignore extra clicks while a request is in flight.
    if (status === "sending") return;

    if (isSpamSubmission(honeypot, mountedAt.current)) {
      // Fake success so bots gain nothing from probing the form.
      setStatus("success");
      return;
    }

    setStatus("sending");
    trackCta("form_submit", formId, page);

    try {
      await submitContactLead({
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        subject,
        message: form.message || "No additional message provided",
        source,
      });
      setStatus("success");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
      // Success-only lead conversion (GA4 + dataLayer). Never fires on click
      // or on an attempt — only after the edge function confirmed the lead.
      trackLeadConversion(source, page);
    } catch (err) {
      console.error("Lead form error:", err);
      setStatus("error");
    }
  };

  return (
    <form onSubmit={handleSubmit} id={formId} data-form={formId} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${formId}-name`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("Name *")}
          </label>
          <input
            type="text"
            id={`${formId}-name`}
            name="name"
            required
            value={form.name}
            onChange={handleChange}
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
            placeholder={t("Your name")}
          />
        </div>
        <div>
          <label htmlFor={`${formId}-company`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("Company")}
          </label>
          <input
            type="text"
            id={`${formId}-company`}
            name="company"
            value={form.company}
            onChange={handleChange}
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
            placeholder={t("Your company")}
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${formId}-email`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("Email *")}
          </label>
          <input
            type="email"
            id={`${formId}-email`}
            name="email"
            required
            value={form.email}
            onChange={handleChange}
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
            placeholder={t("you@company.com")}
          />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("Phone *")}
          </label>
          <input
            type="tel"
            id={`${formId}-phone`}
            name="phone"
            required
            value={form.phone}
            onChange={handleChange}
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
            placeholder={t("(727) 000-0000")}
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${formId}-message`} className="mb-1.5 block text-sm font-medium text-slate-700">
          {t("How can we help?")}
        </label>
        <textarea
          id={`${formId}-message`}
          name="message"
          rows={4}
          value={form.message}
          onChange={handleChange}
          className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
          placeholder={t("Tell us what's not working, what you're planning, or where technology is slowing your team down.")}
        />
      </div>

      {/* Honeypot — invisible to humans, irresistible to bots. Screen readers
          skip it via aria-hidden; keyboard users skip it via tabIndex=-1. */}
      <div aria-hidden="true" className="hidden">
        <label htmlFor={`${formId}-${HONEYPOT_FIELD}`}>Leave this field empty</label>
        <input
          type="text"
          id={`${formId}-${HONEYPOT_FIELD}`}
          name={HONEYPOT_FIELD}
          value={honeypot}
          onChange={handleHoneypotChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <CtaButton
        type="submit"
        ctaName={`${formId}-submit`}
        page={page}
        disabled={status === "sending"}
        className="w-full sm:w-auto"
      >
        {status === "sending" ? t("Sending...") : t(submitLabel)}
      </CtaButton>

      {/* Success message — GTM's Google Ads conversion trigger watches for
          this exact `p.text-emerald-600` selector to become visible. It can
          only render here, after a confirmed successful submission, so the
          conversion cannot fire from a click alone. Keep the class even if
          the rest of the styling changes; id/data-attr are for future,
          more specific triggers. */}
      {status === "success" && (
        <p
          id="lead-form-success"
          data-conversion-event="lead-form-success"
          role="status"
          aria-live="polite"
          className="text-sm font-medium text-emerald-600"
        >
          {t("Thanks! Your request has been received. A SightLine Networks engineer will follow up within one business day.")}
        </p>
      )}
      {status === "error" && (
        <p className="text-sm font-medium text-red-600" role="alert">
          {t("Something went wrong. Please call us at")} {PHONE} {t("or email")} {EMAIL}.
        </p>
      )}
      {/* Bot-only fake success: deliberately does NOT use the emerald
          class/id above so GTM's Ads conversion trigger cannot see it. */}
      {status === "spam" && (
        <p className="text-sm font-medium text-slate-600" aria-hidden="true">
          {t("Thanks! Your request has been received. A SightLine Networks engineer will follow up within one business day.")}
        </p>
      )}
    </form>
  );
}

/** Bottom sticky bar on mobile only — call or jump straight to the lead form.
 *  `label` lets each landing page offer its own conversion (assessment, survey,
 *  estimate) instead of the generic IT consultation. */
function MobileStickyCta({
  onConsultClick,
  ctaName,
  page,
  label = "Free IT Consultation",
}: {
  onConsultClick: () => void;
  ctaName: string;
  page: PageKey;
  label?: string;
}) {
  const t = useT();
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-2 border-t border-slate-200 bg-white/95 p-3 shadow-[0_-4px_12px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
      <a
        href={PHONE_HREF}
        className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
        onClick={() => trackCta("phone_click", `${ctaName}_sticky_phone`, page)}
      >
        <Phone className="h-4 w-4" /> {t("Call Us")}
      </a>
      <button
        onClick={onConsultClick}
        data-cta={`${ctaName}-sticky`}
        data-page={page}
        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-sm"
      >
        {t(label)}
      </button>
    </div>
  );
}

// =========================================================================
// MANAGED IT SERVICES — GOOGLE ADS LANDING PAGE (/managed-it-services)
// =========================================================================

function ManagedItServicesLandingPage({
  setPage,
}: {
  setPage: (page: PageKey) => void;
}) {
  const t = useT();

  const scrollToLeadForm = () => {
    document
      .getElementById("managed-it-lead-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const painPoints: Array<{ icon: typeof Zap; title: string }> = [
    { icon: Zap, title: "Slow computers and networks" },
    { icon: Wifi, title: "Wi-Fi and connectivity problems" },
    { icon: Mail, title: "Microsoft 365 issues" },
    { icon: Monitor, title: "Employee onboarding and offboarding" },
    { icon: Lock, title: "Cybersecurity concerns" },
    { icon: Wrench, title: "Recurring IT problems with no dedicated IT team" },
  ];

  return (
    // Bottom padding on mobile only, clearing the sticky call/consult bar.
    <main className="pb-24 lg:pb-0">
      <ServiceHero
        eyebrow="Serving Tampa Bay businesses"
        title="Managed IT Services for Tampa Bay Businesses"
        subtitle="Keep your business running with reliable IT support, Microsoft 365 management, cybersecurity, network management, and ongoing technology support from a local team."
        primaryLabel="Schedule a Free IT Consultation"
        onPrimary={() => {
          trackCta("quote_click", "managed_it_hero", "managed-it-services");
          scrollToLeadForm();
        }}
        ctaName="managed-it-hero"
        page="managed-it-services"
      />

      <TrustBar
        items={[
          { icon: MapPin, label: "Local Tampa Bay Support" },
          { icon: Building2, label: "Business-Focused IT" },
          { icon: Shield, label: "Network & Cybersecurity Expertise" },
          { icon: Headphones, label: "Responsive Technical Support" },
        ]}
      />

      {/* Problem / pain points */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("The daily reality")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Stop Losing Time to IT Problems")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "Technology problems interrupt your employees, slow down productivity, and put your business at risk. Most of these issues sound familiar:"
            )}
          </p>
        </div>

        <div className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2">
          {painPoints.map(({ icon: Icon, title }) => (
            <div
              key={title}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
                <Icon className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium text-slate-700">{t(title)}</span>
            </div>
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-slate-600 leading-relaxed">
          {t(
            "SightLine Networks manages these issues so your team can stay focused on running the business instead of troubleshooting technology."
          )}
        </p>
      </section>

      {/* Managed IT services */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("What's included")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("IT Support Built Around Your Business")}
            </h2>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={Headphones}
              title={t("Managed IT Support")}
              text={t("Ongoing support for your users, computers, systems, and everyday technology issues.")}
            />
            <FeatureCard
              icon={Mail}
              title={t("Microsoft 365")}
              text={t("Administration, email, licensing, security, account management, and user support.")}
            />
            <FeatureCard
              icon={Wifi}
              title={t("Network Management")}
              text={t("Business Wi-Fi, switches, firewalls, VPNs, network monitoring, and troubleshooting.")}
            />
            <FeatureCard
              icon={Shield}
              title={t("Cybersecurity")}
              text={t("Help protect users, devices, accounts, and business data using modern security practices.")}
            />
            <FeatureCard
              icon={HardDrive}
              title={t("Backup & Recovery")}
              text={t("Help protect critical company data and prepare your business for hardware failures, outages, and other disruptions.")}
            />
            <FeatureCard
              icon={LineChart}
              title={t("IT Consulting")}
              text={t("Technology planning, infrastructure upgrades, migrations, vendor coordination, and long-term IT strategy.")}
            />
          </div>
        </div>
      </section>

      {/* Who we help */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Who we help")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Managed IT for Small and Midsize Businesses")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "SightLine Networks works with organizations that need professional IT capabilities without necessarily maintaining a full internal IT department."
            )}
          </p>
        </div>
        <div className="mt-10">
          <IndustryGrid
            items={[
              "Professional Offices",
              "Manufacturing",
              "Construction",
              "Warehouses",
              "Retail",
              "Multi-Location Businesses",
            ]}
          />
        </div>
      </section>

      {/* Why SightLine */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Why SightLine Networks")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Why Businesses Choose SightLine Networks")}
            </h2>
          </div>
          <div className="mt-12">
            <WhyChooseUs
              items={[
                {
                  icon: MapPin,
                  title: "Local Support",
                  text: "Work with a local technology company instead of depending entirely on a national call center.",
                },
                {
                  icon: Globe,
                  title: "One Technology Partner",
                  text: "IT, networks, Wi-Fi, Microsoft 365, infrastructure, security cameras, and structured cabling — all from one team.",
                },
                {
                  icon: Building2,
                  title: "Business-Focused Solutions",
                  text: "Our services are built for organizations and their teams, not residential computer repair.",
                },
                {
                  icon: Server,
                  title: "Practical Technology Expertise",
                  text: "Hands-on experience with business technology including Ubiquiti UniFi, Cisco, Microsoft, and Synology environments.",
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Real work / projects */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Project spotlights")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Technology Built for Real Businesses")}
          </h2>
          <p className="mt-3 text-sm italic text-slate-500">
            {t("Project spotlights coming soon — real Tampa Bay project photos and details will be added here.")}
          </p>
        </div>
        {/*
          PLACEHOLDER — TODO(marketing): replace these tiles with real project
          photos (network racks/server rooms, UniFi installs, access points,
          structured cabling, camera installs, technicians on-site) once
          supplied. Do not fill with stock imagery — see the handoff notes for
          this page for what to send over.
        */}
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Server, label: "Network Rack Installation" },
            { icon: Wifi, label: "UniFi Access Point Deployment" },
            { icon: Camera, label: "Security Camera Installation" },
            { icon: HardDrive, label: "Structured Cabling" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center"
            >
              <Icon className="h-7 w-7 text-slate-400" />
              <span className="text-sm font-medium text-slate-500">{t(label)}</span>
              <span className="text-xs uppercase tracking-wide text-slate-400">
                {t("Photo coming soon")}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Service area */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center">
          <SectionLabel>{t("Service area")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Serving Businesses Throughout Tampa Bay")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600 leading-relaxed">
            {t("SightLine Networks provides business technology services throughout the Tampa Bay area, including:")}
          </p>
          <div className="mt-8">
            <ServiceArea cities={["Tampa", "Clearwater", "St. Petersburg", "Largo", "Pinellas Park", "Brandon"]} />
          </div>
        </div>
      </section>

      {/* Internal links to related services */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {t("Related services")}
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <PageLink
              to="network-installation"
              setPage={setPage}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:border-sky-300 hover:bg-slate-50"
            >
              {t("Network installation & Wi-Fi")}
              <ArrowRight className="h-4 w-4 text-sky-600" />
            </PageLink>
            <PageLink
              to="business-wifi"
              setPage={setPage}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:border-sky-300 hover:bg-slate-50"
            >
              {t("Fix slow business Wi-Fi")}
              <ArrowRight className="h-4 w-4 text-sky-600" />
            </PageLink>
          </div>
        </div>
      </section>

      <LeadCta
        title="Ready for Better IT Support?"
        text="Tell us what's not working, what you're planning, or where your technology is creating problems. We'll help you determine the right next step."
        primaryLabel="Schedule a Free IT Consultation"
        onPrimary={() => {
          trackCta("quote_click", "managed_it_bottom", "managed-it-services");
          scrollToLeadForm();
        }}
        ctaName="managed-it-bottom"
        page="managed-it-services"
      />

      {/* Lead form */}
      <section id="managed-it-lead-form" className="mx-auto max-w-3xl scroll-mt-20 px-6 py-20">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("Request Your Free IT Consultation")}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {t("Fill out the form and our Tampa Bay team will follow up within one business day.")}
            </p>
          </div>
          <div className="mt-8">
            <LeadForm
              page="managed-it-services"
              source="managed-it-services-landing"
              formId="managed-it-services-form"
              subject="Managed IT Services"
              submitLabel="Request My Free Consultation"
            />
          </div>
        </div>
      </section>

      <MobileStickyCta
        onConsultClick={() => {
          trackCta("quote_click", "managed_it_sticky", "managed-it-services");
          scrollToLeadForm();
        }}
        ctaName="managed-it"
        page="managed-it-services"
      />
    </main>
  );
}

// =========================================================================
// MAIN APP COMPONENT
// =========================================================================

export default function SightlineWebsite() {
  // Every page is a real, crawlable URL in both languages — the initial state
  // is read from the address bar so direct hits, bookmarks, and Google's
  // crawler all land on the right page in the right language. See
  // src/i18n/routes.ts for the URL table and public/.htaccess for the SPA
  // fallback that lets the server hand these paths to index.html.
  //
  // resolveInitialLocation() additionally applies the first-visit
  // browser-language switch (bare "/" only, never for bots or the
  // prerenderer, never once a language has been chosen manually) — see
  // src/i18n/detect.ts for the full guard list.
  const [location, setLocation] = useState<{ page: PageKey; lang: Lang }>(() =>
    typeof window === "undefined"
      ? { page: "home", lang: "en" }
      : resolveInitialLocation(window.location.pathname)
  );
  const { page, lang } = location;
  const [mobileOpen, setMobileOpen] = useState(false);
  // Suppress the scroll-to-top effect for back/forward navigations, where the
  // browser restores the previous scroll position itself.
  const [isPopNavigation, setIsPopNavigation] = useState(false);

  /** Navigate within the current language, pushing a real URL. */
  const setPage = useCallback(
    (next: PageKey) => {
      setLocation((prev) => {
        if (typeof window !== "undefined") {
          window.history.pushState({}, "", pathFor(next, prev.lang));
        }
        return { page: next, lang: prev.lang };
      });
      setIsPopNavigation(false);
    },
    []
  );

  /** Switch language, staying on the equivalent page in the other tree. */
  const setLang = useCallback((next: Lang) => {
    // An explicit choice is permanent: it pins the language for future visits
    // and stops browser-language auto-detection from ever overriding it.
    writeLangPreference(next);
    setLocation((prev) => {
      if (prev.lang === next) return prev;
      if (typeof window !== "undefined") {
        window.history.pushState({}, "", pathFor(prev.page, next));
      }
      return { page: prev.page, lang: next };
    });
    setIsPopNavigation(false);
  }, []);

  // Lead attribution: on first load, remember how the visitor arrived (landing
  // URL, referrer, utm_* / gclid) in sessionStorage so that whichever page they
  // finally submit a lead from can attach it to the request. First-URL-wins.
  useEffect(() => {
    captureLeadAttribution();
  }, []);

  // Back/forward buttons must move between pages, not exit the site.
  useEffect(() => {
    const onPopState = () => {
      setIsPopNavigation(true);
      setLocation(resolveLocation(window.location.pathname));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Scroll to top on page change (but not on back/forward — see above)
  useEffect(() => {
    if (!isPopNavigation) {
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
    setMobileOpen(false);
    // isPopNavigation is intentionally excluded: this should run on navigation,
    // not when the flag itself flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, lang]);

  // On a direct load of e.g. /privacy#do-not-sell, scroll to the anchor once
  // the page has mounted (after the scroll-to-top effect above has run).
  useEffect(() => {
    if (typeof window === "undefined" || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => window.clearTimeout(t);
  }, []);

  const CurrentPage = useMemo(() => {
    switch (page) {
      case "home":
        return <HomePage setPage={setPage} />;
      case "services":
        return <ServicesPage setPage={setPage} />;
      case "hosting":
        return <HostingPage setPage={setPage} />;
      case "solutions":
        return <SolutionsPage setPage={setPage} />;
      case "areas":
        return <AreasPage setPage={setPage} />;
      case "about":
        return <AboutPage setPage={setPage} />;
      case "contact":
        return <ContactPage setPage={setPage} />;
      case "managed-it":
        return <ManagedItPage setPage={setPage} />;
      case "managed-it-services":
        return <ManagedItServicesLandingPage setPage={setPage} />;
      case "business-wifi":
        return <BusinessWifiLanding setPage={setPage} />;
      case "unifi-installer":
        return <UnifiInstallerLanding setPage={setPage} />;
      case "camera-installation":
        return <CameraInstallationLanding setPage={setPage} />;
      case "structured-cabling":
        return <StructuredCablingLanding setPage={setPage} />;
      case "network-installation":
        return <NetworkInstallationPage setPage={setPage} />;
      case "security-cameras":
        return <SecurityCamerasPage setPage={setPage} />;
      case "server-cloud":
        return <ServerCloudPage setPage={setPage} />;
      case "website-development":
        return <WebsiteDevelopmentPage setPage={setPage} />;
      case "it-consulting":
        return <ItConsultingPage setPage={setPage} />;
      case "privacy":
        return <PrivacyPage setPage={setPage} />;
      default:
        return <HomePage setPage={setPage} />;
    }
    // Deliberately not keyed on `lang`: every page component reads the
    // translator from context, and React re-renders context consumers even when
    // a memoized parent element bails out. Re-keying here would just discard
    // the memo on every language switch for no benefit.
  }, [page, setPage]);

  return (
    <LanguageProvider lang={lang} setLang={setLang}>
      <div className="min-h-screen bg-white text-slate-900 antialiased">
        {/* SEO manager — updates title, canonical, hreflang, and schema */}
        <SeoManager page={page} lang={lang} />

        {/* Skip to main content (accessibility) */}
        <SkipLink />

        <Header
          setPage={setPage}
          page={page}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />

        <div id="main-content">{CurrentPage}</div>

        <Footer setPage={setPage} page={page} />

        {/* CCPA/CPRA notice-only privacy banner — global, dismissible, shown
            once per consent version. Rendered last so it overlays page content. */}
        <PrivacyNoticeBanner setPage={setPage} />
      </div>
    </LanguageProvider>
  );
}

/** Rendered inside the provider so its label can be translated. */
function SkipLink() {
  const t = useT();
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-sky-600 focus:px-4 focus:py-2 focus:text-white"
    >
      {t("Skip to main content")}
    </a>
  );
}

// Re-exports of the shared landing-kit components (ServiceHero, LeadForm,
// CtaButton, …) for the Ads landing pages in src/landing/*. The imports are
// circular by design — the landing modules use everything at render time, not
// at module-evaluation time, so the cycle resolves safely. LeadForm's success
// markup (`p.text-emerald-600`, id="lead-form-success") is the exact element
// GTM's Google Ads conversion trigger watches for: never change it.
export {
  trackCta,
  trackLeadConversion,
  CtaButton,
  PageLink,
  LeadForm,
  MobileStickyCta,
  ServiceHero,
  TrustBar,
  IndustryGrid,
  WhyChooseUs,
  ServiceArea,
  LeadCta,
  SectionLabel,
  FeatureCard,
};
